import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Database,
  Server,
  HardDrive,
  Cloud,
  Cloudy,
  Box,
  CalendarClock,
  FileArchive,
  Link2,
  Power,
  Star,
  Download,
  CloudDownload,
  ShieldCheck,
  RotateCcw,
  RefreshCw,
  Plus,
  Save,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Clock,
  Play,
  Pause,
  Square,
  Activity,
  XCircle,
  FolderOpen,
  HardDriveDownload,
  Network,
  Upload,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Copy,
  ExternalLink,
  Calendar,
  User,
  Hash,
  Archive,
  Layers,
  Eraser,
  Merge,
  ShieldAlert,
  Info,
  Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { TimePicker } from '../../components/ui/DatePicker';
import { Badge } from '../../components/ui/badge';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  useBackupOverview,
  useBackups,
  useBackupDestinations,
  useSyncDestinations,
  useBackupSchedule,
  useBackupModules,
  useBackupOAuthStatus,
  useCreateBackup,
  useDeleteBackup,
  useDeleteBackups,
  useVerifyBackup,
  useRestoreBackup,
  useSaveDestination,
  useTestDestination,
  useDeleteDestination,
  useUpdateSchedule,
  useRunScheduleNow,
  useEncryptionStatus,
  useReencryptBackup,
  usePreviewRestore,
  usePreviewUploadRestore,
  useInspectRestoreUpload,
  useRestoreUpload,
  useBackupRun,
  usePauseBackup,
  useResumeBackup,
  useCancelBackup,
  useDestroyJob,
  useQueueStatus,
  usePauseQueue,
  useResumeQueue,
  useRestoreRun,
  usePauseRestore,
  useResumeRestore,
  useCancelRestore,
  useDestroyRestoreJob,
  useRetryBackup,
  useRetryRestore,
  useStorageHealth,
} from '../../services/httpServices/queries/useBackups';
import {
  backupService,
  CreateBackupPayload,
  RestorePayload,
} from '../../services/httpServices/backupService';
import {
  isLocalFolderSupported,
  pickLocalBackupFolder,
  getLocalBackupFolder,
  clearLocalBackupFolder,
  ensureLocalBackupFolderAccess,
  writeBackupToLocalFolder,
} from '../../lib/localBackupFolder';
import {
  BackupDestination,
  BackupProgress,
  BackupProvider,
  BackupRecord,
  BackupSchedule,
  DataModule,
  RestoreMode,
  RestorePreview,
  RestoreUploadInspection,
} from '../../types/backup';
import {
  startBackupSocket,
  stopBackupSocket,
  subscribeBackupSocket,
  useBackupSocketConnected,
} from '../../utils/backupSocket';
import { cn } from '../../lib/utils';

type TabKey = 'destinations' | 'schedule' | 'history';

const TAB_KEYS: TabKey[] = ['destinations', 'schedule', 'history'];

/** Keyword the operator must type before a normal restore can be started. */
const RESTORE_KEYWORD = 'RESTORE';

/** Extra phrase required for a clean restore (wipes all existing data). */
const CLEAN_RESTORE_KEYWORD = 'DELETE ALL DATA';

interface FieldDef {
  key: string;
  label: string;
  placeholder?: string;
  secret?: boolean;
  hint?: string;
  type?: 'text' | 'number' | 'boolean' | 'url';
  /** OAuth app credentials — hidden when the app provides them. */
  credential?: boolean;
}

const PROVIDER_META: Record<
  BackupProvider,
  {
    label: string;
    description: string;
    icon: React.ElementType;
    oauth: boolean;
  }
> = {
  local: {
    label: 'Local / Server',
    description:
      'Store backups on the server, or save a copy straight to a folder on this PC.',
    icon: HardDrive,
    oauth: false,
  },
  s3: {
    label: 'S3-compatible',
    description: 'Cloudflare R2, AWS S3, Wasabi, Backblaze B2 or MinIO.',
    icon: Server,
    oauth: false,
  },
  webdav: {
    label: 'WebDAV',
    description:
      'PikPak (Premium), Nextcloud, ownCloud, Koofr, Box and other WebDAV servers.',
    icon: Network,
    oauth: false,
  },
  google_drive: {
    label: 'Google Drive',
    description: 'Upload backups to a folder in your Google Drive.',
    icon: Cloud,
    oauth: true,
  },
  dropbox: {
    label: 'Dropbox',
    description: 'Upload backups to your Dropbox account.',
    icon: Box,
    oauth: true,
  },
  onedrive: {
    label: 'OneDrive',
    description: 'Upload backups to your Microsoft OneDrive.',
    icon: Cloudy,
    oauth: true,
  },
};

const PROVIDER_FIELDS: Record<BackupProvider, FieldDef[]> = {
  local: [
    {
      key: 'directory',
      label: 'Server backup directory',
      placeholder: '/var/backups/firstaid-os',
      hint: 'Path on the server that hosts FastAid OS — not your PC. Leave blank to use the default "backups" folder next to the backend.',
    },
  ],
  s3: [
    {
      key: 'endpoint',
      label: 'Endpoint URL',
      placeholder: 'https://<account>.r2.cloudflarestorage.com',
    },
    { key: 'region', label: 'Region', placeholder: 'auto' },
    { key: 'bucket', label: 'Bucket', placeholder: 'firstaid-os-backups' },
    { key: 'prefix', label: 'Folder prefix', placeholder: 'backups' },
    { key: 'accessKeyId', label: 'Access key ID' },
    { key: 'secretAccessKey', label: 'Secret access key', secret: true },
    {
      key: 'forcePathStyle',
      label: 'Force path-style URLs',
      type: 'boolean',
      hint: 'Enable for MinIO and most self-hosted S3 services.',
    },
  ],
  webdav: [
    {
      key: 'url',
      label: 'WebDAV URL',
      type: 'url',
      placeholder: 'your-webdav-host/dav',
      hint: 'The full WebDAV server URL from your provider. PikPak: Settings → Access & Integrations → WebDAV. Nextcloud: usually https://your-host/remote.php/dav/files/<user>. PikPak WebDAV is read-only, so it can list and restore archives but cannot receive new backups.',
    },
    { key: 'username', label: 'Username' },
    { key: 'password', label: 'Password', secret: true },
    {
      key: 'prefix',
      label: 'Folder prefix',
      placeholder: 'FastAid OS Backups',
      hint: 'Optional folder that backups are stored in. It is created automatically if missing.',
    },
  ],
  google_drive: [
    { key: 'clientId', label: 'OAuth Client ID', credential: true },
    {
      key: 'clientSecret',
      label: 'OAuth Client secret',
      secret: true,
      credential: true,
    },
    {
      key: 'folderId',
      label: 'Folder ID (optional)',
      hint: 'Leave blank to upload to the root of My Drive.',
    },
  ],
  dropbox: [
    { key: 'appKey', label: 'App key', credential: true },
    { key: 'appSecret', label: 'App secret', secret: true, credential: true },
    { key: 'folder', label: 'Folder', placeholder: 'FastAid OS Backups' },
  ],
  onedrive: [
    { key: 'clientId', label: 'Application (client) ID', credential: true },
    {
      key: 'clientSecret',
      label: 'Client secret',
      secret: true,
      credential: true,
    },
    {
      key: 'tenantId',
      label: 'Directory (tenant) ID',
      placeholder: 'common',
      credential: true,
    },
    { key: 'folder', label: 'Folder', placeholder: 'FastAid OS Backups' },
  ],
};

const STATUS_BADGE: Record<
  BackupRecord['status'],
  { variant: 'success' | 'danger' | 'warning' | 'default'; label: string }
> = {
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'danger', label: 'Failed' },
  running: { variant: 'warning', label: 'Running' },
  queued: { variant: 'default', label: 'Queued' },
  pending: { variant: 'default', label: 'Pending' },
  cancelled: { variant: 'default', label: 'Cancelled' },
};

function formatBytes(value: string | number | undefined): string {
  const bytes = Number(value || 0);
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Human-readable duration between two timestamps (e.g. "2m 14s"). */
function formatDuration(start?: string, end?: string): string {
  if (!start || !end) return '—';
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return '—';
  const seconds = Math.round((to - from) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const minutesRest = minutes % 60;
  return minutesRest ? `${hours}h ${minutesRest}m` : `${hours}h`;
}

/**
 * Returns the page buttons to render: all pages when there are few, otherwise
 * the first/last plus a window around the current page with `'gap'` markers.
 */
function paginationWindow(
  current: number,
  total: number,
): Array<number | 'gap'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, total, current]);
  for (const delta of [-1, 1, -2, 2]) {
    const page = current + delta;
    if (page >= 1 && page <= total) pages.add(page);
  }
  if (current <= 3) [2, 3, 4].forEach((page) => pages.add(page));
  if (current >= total - 2) {
    [total - 1, total - 2, total - 3].forEach((page) => pages.add(page));
  }

  const sorted = Array.from(pages)
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);

  const result: Array<number | 'gap'> = [];
  let previous = 0;
  for (const page of sorted) {
    if (page - previous > 1) result.push('gap');
    result.push(page);
    previous = page;
  }
  return result;
}

/**
 * Narrows the module catalog to the modules actually present in a backup
 * archive so the restore picker can't offer data that isn't there.
 */
function modulesAvailableInRecord(
  modules: DataModule[],
  record: BackupRecord | null,
): DataModule[] {
  if (!record) return modules;
  const meta = record.metadata || {};
  if (meta.includesAllModules || !Array.isArray(meta.tables)) return modules;
  const tables = new Set<string>(meta.tables as string[]);
  return modules.filter((module) =>
    module.tables.some((table) => tables.has(table)),
  );
}

/**
 * Builds the restore request body. A clean restore always keeps its safety
 * backup (the server refuses to run without one) and ignores the module
 * selection, because it imports the whole archive.
 */
function buildRestorePayload(options: {
  includeDatabase: boolean;
  includeAttachments: boolean;
  safetyBackup: boolean;
  modules: string[];
  mode: RestoreMode;
  confirmText: string;
}): RestorePayload {
  const clean = options.mode === 'clean';
  return {
    confirm: true,
    includeDatabase: options.includeDatabase,
    includeAttachments: options.includeAttachments,
    safetyBackup: clean ? true : options.safetyBackup,
    modules:
      clean || !options.includeDatabase ? [] : options.modules,
    mode: options.mode,
    // Kept for older servers that only understand the conflict strategy.
    conflictStrategy: options.mode === 'skip' ? 'skip' : 'overwrite',
    ...(clean ? { confirmText: options.confirmText } : {}),
  };
}

export function BackupSettings() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: TabKey = TAB_KEYS.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : 'destinations';

  /** Keeps the active tab in the URL so it can be bookmarked/shared. */
  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'destinations') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detailsRecord, setDetailsRecord] = useState<BackupRecord | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const overview = useBackupOverview();
  const destinations = useBackupDestinations();
  const schedule = useBackupSchedule();
  const modules = useBackupModules();
  const oauthStatus = useBackupOAuthStatus();
  const backups = useBackups({
    page,
    limit: pageSize,
    status: statusFilter || undefined,
  });

  const createBackup = useCreateBackup();
  const deleteBackup = useDeleteBackup();
  const deleteBackups = useDeleteBackups();
  const verifyBackup = useVerifyBackup();
  const restoreBackup = useRestoreBackup();
  const inspectUpload = useInspectRestoreUpload();
  const restoreUpload = useRestoreUpload();
  const syncDestinations = useSyncDestinations();
  const saveDestination = useSaveDestination();
  const testDestination = useTestDestination();
  const deleteDestination = useDeleteDestination();
  const updateSchedule = useUpdateSchedule();
  const runScheduleNow = useRunScheduleNow();
  const encryption = useEncryptionStatus();
  const reencryptBackup = useReencryptBackup();
  const previewRestore = usePreviewRestore();
  const previewUpload = usePreviewUploadRestore();
  const socketLive = useBackupSocketConnected();
  const pauseBackup = usePauseBackup();
  const resumeBackup = useResumeBackup();
  const cancelBackup = useCancelBackup();
  const destroyJob = useDestroyJob();
  const queueStatus = useQueueStatus();
  const pauseQueue = usePauseQueue();
  const resumeQueue = useResumeQueue();
  const storageHealth = useStorageHealth();

  // Live events: patch the run query in place (the progress card re-renders
  // without a request) and refresh the lists when something finishes.
  useEffect(() => {
    startBackupSocket();
    const unsubscribe = subscribeBackupSocket((event) => {
      if (event.type === 'backup:progress') {
        const { recordId, progress } = event.payload;
        if (!recordId) return;
        const key = ['backup', 'run', recordId];
        const current = queryClient.getQueryData<any>(key);
        if (!current?.data) return;
        queryClient.setQueryData(key, {
          ...current,
          data: { ...current.data, state: 'running', progress },
        });
        return;
      }

      if (event.type === 'backup:finished') {
        const { recordId, state, record, progress } = event.payload;
        const key = ['backup', 'run', recordId];
        const current = queryClient.getQueryData<any>(key);
        if (current?.data) {
          queryClient.setQueryData(key, {
            ...current,
            data: {
              ...current.data,
              state,
              record: record ?? current.data.record ?? null,
              progress: progress ?? current.data.progress ?? null,
            },
          });
        }
        queryClient.invalidateQueries({ queryKey: ['backup', 'records'] });
        queryClient.invalidateQueries({ queryKey: ['backup', 'overview'] });
        queryClient.invalidateQueries({ queryKey: ['backup', 'queue'] });
        return;
      }

      if (event.type === 'restore:progress') {
        const { jobId, progress } = event.payload;
        const key = ['backup', 'restore', jobId];
        const current = queryClient.getQueryData<any>(key);
        if (!current?.data) return;
        queryClient.setQueryData(key, {
          ...current,
          data: { ...current.data, state: 'running', progress },
        });
        return;
      }

      if (event.type === 'restore:finished') {
        const { jobId, state, result, error } = event.payload;
        const key = ['backup', 'restore', jobId];
        const current = queryClient.getQueryData<any>(key);
        if (current?.data) {
          queryClient.setQueryData(key, {
            ...current,
            data: {
              ...current.data,
              state,
              result: result ?? current.data.result ?? null,
              error: error ?? current.data.error ?? null,
            },
          });
        }
        queryClient.invalidateQueries({ queryKey: ['backup'] });
        return;
      }

      if (event.type === 'queue:changed') {
        queryClient.invalidateQueries({ queryKey: ['backup', 'records'] });
        queryClient.invalidateQueries({ queryKey: ['backup', 'overview'] });
        queryClient.invalidateQueries({ queryKey: ['backup', 'queue'] });
        return;
      }
    });

    return () => {
      unsubscribe();
      stopBackupSocket();
    };
  }, [queryClient]);

  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [restorePreview, setRestorePreview] = useState<RestorePreview | null>(
    null,
  );
  const [uploadPreview, setUploadPreview] = useState<RestorePreview | null>(
    null,
  );
  // With the socket connected, progress arrives as events and the 1s poll is
  // switched off; `false` makes the hooks fall back to polling.
  const run = useBackupRun(activeRunId, socketLive);

  const [restoreJobId, setRestoreJobId] = useState<string | null>(null);
  const restoreRun = useRestoreRun(restoreJobId, socketLive);
  const pauseRestore = usePauseRestore();
  const resumeRestore = useResumeRestore();
  const cancelRestore = useCancelRestore();
  const destroyRestoreJob = useDestroyRestoreJob();
  const retryBackup = useRetryBackup();
  const retryRestore = useRetryRestore();

  const [createOpen, setCreateOpen] = useState(false);
  const [createDest, setCreateDest] = useState<string>('');
  const [createDb, setCreateDb] = useState(true);
  const [createFiles, setCreateFiles] = useState(true);
  const [createModules, setCreateModules] = useState<string[]>([]);

  const [configProvider, setConfigProvider] = useState<BackupProvider | null>(
    null,
  );
  const [configForm, setConfigForm] = useState<Record<string, any>>({});
  const [configUrlScheme, setConfigUrlScheme] = useState<'http' | 'https'>(
    'https',
  );
  const [configEnabled, setConfigEnabled] = useState(true);
  const [configDefault, setConfigDefault] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Optional PC folder (File System Access API) that manual backups can also
  // be written to. The handle itself lives in IndexedDB.
  const [localFolderName, setLocalFolderName] = useState<string | null>(null);
  const [localFolderSupported, setLocalFolderSupported] = useState(false);
  const [saveToPc, setSaveToPc] = useState(false);

  /** Resets the Create-backup form so reopening starts fresh. */
  const resetCreateForm = () => {
    setCreateDest('');
    setCreateDb(true);
    setCreateFiles(true);
    setCreateModules([]);
    setSaveToPc(false);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    resetCreateForm();
  };
  const [savingToPcId, setSavingToPcId] = useState<string | null>(null);

  const [deleteBackupId, setDeleteBackupId] = useState<string | null>(null);
  const [deleteProvider, setDeleteProvider] = useState<BackupProvider | null>(
    null,
  );
  const [restoreRecord, setRestoreRecord] = useState<BackupRecord | null>(null);
  const [restoreDb, setRestoreDb] = useState(true);
  const [restoreFiles, setRestoreFiles] = useState(true);
  const [restoreSafety, setRestoreSafety] = useState(true);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoreModules, setRestoreModules] = useState<string[]>([]);
  const [restoreMode, setRestoreMode] = useState<RestoreMode>('merge');

  // Upload-and-restore (restore a downloaded .hmsbkup file).
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadInfo, setUploadInfo] = useState<RestoreUploadInspection | null>(
    null,
  );
  const [uploadDb, setUploadDb] = useState(true);
  const [uploadFiles, setUploadFiles] = useState(true);
  const [uploadSafety, setUploadSafety] = useState(true);
  const [uploadModules, setUploadModules] = useState<string[]>([]);
  const [uploadConfirm, setUploadConfirm] = useState('');
  const [uploadMode, setUploadMode] = useState<RestoreMode>('merge');

  const records = backups.data?.data || [];
  const destinationList = destinations.data?.data || [];
  const moduleList: DataModule[] = modules.data?.data || [];
  const scheduleData: BackupSchedule | undefined = schedule.data?.data;
  const stats = overview.data?.data;
  const queue = queueStatus.data?.data;
  const health = storageHealth.data?.data;
  const enabledDestinations = destinationList.filter(
    (destination) => destination.isEnabled,
  ).length;
  const dataTableCount = new Set(
    moduleList.flatMap((module) => module.tables),
  ).size;

  // ── Backups list pagination + selection ─────────────────────────────────
  const totalRecords = backups.data?.meta?.total ?? records.length;
  const totalPages = Math.max(1, backups.data?.meta?.totalPages ?? 1);
  const pageStart = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(
    totalRecords,
    (page - 1) * pageSize + records.length,
  );
  const pageIds = records.map((record) => record.id);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const someOnPageSelected =
    !allOnPageSelected && pageIds.some((id) => selectedIds.includes(id));

  // Moving to another page or changing the page size clears the selection so a
  // bulk delete can never touch rows the operator can no longer see.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, pageSize, statusFilter]);

  // A refetch can remove or re-order rows; keep only ids still visible.
  useEffect(() => {
    setSelectedIds((current) =>
      current.filter((id) =>
        (backups.data?.data || []).some((record) => record.id === id),
      ),
    );
  }, [backups.data]);

  const toggleSelectAll = () => {
    setSelectedIds((current) =>
      allOnPageSelected
        ? current.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...current, ...pageIds])),
    );
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selected) => selected !== id)
        : [...current, id],
    );
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    const ids = [...selectedIds];
    deleteBackups.mutate(ids, {
      onSuccess: (response) => {
        const { deleted, failed } = response.data;
        setBulkDeleteOpen(false);
        setSelectedIds([]);
        if (failed.length > 0) {
          toast.error(
            `${deleted} deleted · ${failed.length} failed: ${failed[0].message}`,
          );
        } else {
          toast.success(`Deleted ${deleted} backup${deleted === 1 ? '' : 's'}`);
        }
        // Step back a page when the current one is now empty.
        if (page > 1 && deleted >= records.length) {
          setPage((current) => Math.max(1, current - 1));
        }
      },
      onError: (err: any) =>
        toast.error(err?.message || 'Could not delete the selected backups'),
    });
  };

  // Listen for the OAuth popup completing a connection.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'firstaid-backup-oauth') return;
      if (event.data.success) {
        toast.success('Account connected successfully');
        destinations.refetch();
      } else {
        toast.error('Could not connect the storage account');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [destinations]);

  // Detect local-folder support and restore any previously chosen PC folder.
  useEffect(() => {
    setLocalFolderSupported(isLocalFolderSupported());
    let active = true;
    getLocalBackupFolder()
      .then((folder) => {
        if (active && folder) setLocalFolderName(folder.name);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // Backups made by another installation (for example staging pointing at the
  // same WebDAV folder) only exist in storage until they are discovered here.
  const storageScannedRef = useRef(false);
  useEffect(() => {
    if (tab !== 'history' || storageScannedRef.current) return;
    storageScannedRef.current = true;
    syncDestinations.mutate(undefined, {
      onSuccess: (response) => {
        const { imported } = response.data;
        if (imported > 0) {
          toast.success(
            `Found ${imported} backup${imported === 1 ? '' : 's'} in storage`,
          );
        }
      },
      onError: (err: any) =>
        toast.error(
          err?.message ||
            'Could not scan the backup destinations for stored archives',
        ),
    });
  }, [tab, syncDestinations]);

  const openConfig = (destination: BackupDestination) => {
    setConfigProvider(destination.provider);
    const nextForm = { ...(destination.config || {}) };
    if (destination.provider === 'webdav' && nextForm.url) {
      const rawUrl = String(nextForm.url).trim();
      // Tolerate URLs saved before the scheme picker existed, and keep the
      // stored value a full URL that the backend already understands.
      nextForm.url = /^https?:\/\//i.test(rawUrl)
        ? rawUrl
        : `https://${rawUrl}`;
      setConfigUrlScheme(
        nextForm.url.toLowerCase().startsWith('http://') ? 'http' : 'https',
      );
    } else {
      setConfigUrlScheme('https');
    }
    setConfigForm(nextForm);
    setConfigEnabled(destination.isEnabled);
    setConfigDefault(destination.isDefault);
    setTestResult(null);
    setShowAdvanced(false);
  };

  const closeConfig = () => {
    setConfigProvider(null);
    setConfigForm({});
    setConfigUrlScheme('https');
    setTestResult(null);
  };

  const handleSaveDestination = () => {
    if (!configProvider) return;
    saveDestination.mutate(
      {
        provider: configProvider,
        payload: {
          isEnabled: configEnabled,
          isDefault: configDefault,
          config: configForm,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${PROVIDER_META[configProvider].label} saved`);
          closeConfig();
        },
        onError: (err: any) => toast.error(err?.message || 'Could not save'),
      },
    );
  };

  const handleTest = (
    provider: BackupProvider,
    config?: Record<string, any>,
  ) => {
    testDestination.mutate(
      { provider, config },
      {
        onSuccess: (response) => {
          const result = response.data;
          setTestResult(result);
          if (result.ok) toast.success(result.message);
          else toast.error(result.message);
        },
        onError: (err: any) =>
          setTestResult({ ok: false, message: err?.message || 'Test failed' }),
      },
    );
  };

  const handleConnect = async (provider: BackupProvider) => {
    try {
      const response = await backupService.getAuthUrl(provider);
      const { url, redirectUri } = response.data;
      const popup = window.open(
        url,
        'firstaid-backup-oauth',
        'width=620,height=760',
      );
      if (!popup) {
        toast.error('Popup blocked. Allow popups and try again.');
        return;
      }
      toast.info(`Complete the sign-in. Redirect URI: ${redirectUri}`, {
        duration: 8000,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Could not start the connection');
    }
  };

  const handlePickLocalFolder = async () => {
    try {
      const folder = await pickLocalBackupFolder();
      if (!folder) return;
      setLocalFolderName(folder.name);
      toast.success(`Backups can now be saved to "${folder.name}" on this PC`);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      toast.error(err?.message || 'Could not select the folder');
    }
  };

  const handleClearLocalFolder = async () => {
    await clearLocalBackupFolder();
    setLocalFolderName(null);
    setSaveToPc(false);
    toast.success('Folder on this PC removed');
  };

  /** Downloads a stored archive and writes it into the chosen PC folder. */
  const saveRecordToPc = async (record: BackupRecord) => {
    setSavingToPcId(record.id);
    try {
      // Ask for write access first, while the click still counts as a user
      // gesture, then download the archive.
      const folder = await ensureLocalBackupFolderAccess();
      const blob = await backupService.getBackupBlob(record);
      await writeBackupToLocalFolder(folder, blob, record.filename);
      toast.success(`Saved ${record.filename} to "${folder.name}" on this PC`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not save to the local folder');
    } finally {
      setSavingToPcId(null);
    }
  };

  /** Queues a backup and focuses its live progress card. */
  const queueBackup = (
    payload: CreateBackupPayload,
    successMessage = 'Backup queued — it will run in the background',
  ) => {
    createBackup.mutate(payload, {
      onSuccess: (response) => {
        const { record } = response.data;
        toast.success(successMessage);
        setActiveRunId(record.id);
        setTab('history');
      },
      onError: (err: any) => toast.error(err?.message || 'Backup failed'),
    });
  };

  const handleCreate = () => {
    queueBackup({
      destinationId: createDest || undefined,
      includeDatabase: createDb,
      includeAttachments: createFiles,
      modules: createDb ? createModules : [],
    });
    closeCreate();
  };

  // When a run finishes and "also save to this PC" was requested, copy it once.
  const pcSavedRef = useRef<string | null>(null);
  useEffect(() => {
    const data = run.data?.data;
    if (!data?.record) return;
    if (
      data.state === 'completed' &&
      saveToPc &&
      localFolderName &&
      pcSavedRef.current !== data.record.id
    ) {
      pcSavedRef.current = data.record.id;
      void saveRecordToPc(data.record);
    }
  }, [run.data, saveToPc, localFolderName]);

  // Auto-dismiss the live run cards once a backup reaches a terminal state,
  // surfacing the outcome as a toast first. The user can start another run to
  // bring the card back.
  const backupDoneRef = useRef<string | null>(null);
  useEffect(() => {
    const data = run.data?.data;
    if (!activeRunId || !data?.state) return;
    const finished = ['completed', 'failed', 'cancelled'].includes(data.state);
    if (!finished) {
      // A retry / fresh attempt started — allow the outcome toast again.
      if (backupDoneRef.current === activeRunId) backupDoneRef.current = null;
      return;
    }
    if (backupDoneRef.current !== activeRunId) {
      backupDoneRef.current = activeRunId;
      if (data.state === 'completed') {
        toast.success('Backup completed');
      } else if (data.state === 'failed') {
        toast.error(data.record?.errorMessage || 'Backup failed');
      }
    }
    // Keep a failed run on screen so the operator can retry it; close the
    // progress card automatically once the run completes or is cancelled.
    if (data.state === 'failed') return;
    const timer = setTimeout(() => setActiveRunId(null), 4000);
    return () => clearTimeout(timer);
  }, [run.data, activeRunId]);

  // Same for restores — close the progress card when the job finishes.
  const restoreDoneRef = useRef<string | null>(null);
  useEffect(() => {
    const data = restoreRun.data?.data;
    if (!restoreJobId || !data?.state) return;
    const finished = ['completed', 'failed', 'cancelled'].includes(data.state);
    if (!finished) {
      if (restoreDoneRef.current === restoreJobId) restoreDoneRef.current = null;
      return;
    }
    if (restoreDoneRef.current !== restoreJobId) {
      restoreDoneRef.current = restoreJobId;
      if (data.state === 'completed') {
        const result = data.result;
        // Everything the dashboard has loaded may now be stale: drop the
        // browser-side query cache so the restored data appears immediately.
        void queryClient.invalidateQueries();
        if (result?.databaseSkipped) {
          toast.warning(
            'Restore finished without the database — only attachments were restored. Run it again with “Restore database” checked.',
          );
        } else if (
          result &&
          result.restoredRows === 0 &&
          result.restoredAttachments === 0
        ) {
          toast.warning(
            'Restore finished but no rows or files were restored. Check that you restored the archive that contains your data.',
          );
        } else {
          const dependencyNote = result?.dependencyTables?.length
            ? ` · also restored ${result.dependencyTables.length} referenced table(s)`
            : '';
          const cleanNote =
            result?.mode === 'clean'
              ? ` · cleared ${result.clearedTables ?? 0} table(s)` +
                (result.clearedAttachments
                  ? ` and ${result.clearedAttachments} old file(s)`
                  : '')
              : '';
          toast.success(
            result
              ? `Restore completed (${result.restoredRows} row(s), ${result.restoredAttachments} file(s))${dependencyNote}${cleanNote}`
              : 'Restore completed',
          );
        }
      } else if (data.state === 'failed') {
        toast.error(data.error || 'Restore failed');
      }
    }
    if (data.state === 'failed') return;
    const timer = setTimeout(() => setRestoreJobId(null), 4000);
    return () => clearTimeout(timer);
  }, [restoreRun.data, restoreJobId, queryClient]);

  const handleToggle = (
    destination: BackupDestination,
    patch: Partial<BackupDestination>,
  ) => {
    saveDestination.mutate(
      {
        provider: destination.provider,
        payload: {
          isEnabled: patch.isEnabled ?? destination.isEnabled,
          isDefault: patch.isDefault ?? destination.isDefault,
          config: {},
        },
      },
      {
        onSuccess: () => destinations.refetch(),
        onError: (err: any) => toast.error(err?.message || 'Update failed'),
      },
    );
  };

  const handleVerify = (record: BackupRecord) => {
    verifyBackup.mutate(record.id, {
      onSuccess: (response) => {
        const result = response.data;
        if (result.valid) toast.success(result.message);
        else toast.error(result.message);
      },
      onError: (err: any) => toast.error(err?.message || 'Verification failed'),
    });
  };

  /** Re-wraps an archive with the key currently configured on the server. */
  const handleReencrypt = (record: BackupRecord) => {
    reencryptBackup.mutate(record.id, {
      onSuccess: (response) => {
        toast.success(
          `Re-encrypted ${record.filename} (${response.data.entries} entries)`,
        );
        setDetailsRecord(null);
      },
      onError: (err: any) =>
        toast.error(err?.message || 'Could not re-encrypt the backup'),
    });
  };

  /** Scans the destinations for backups this server hasn't recorded yet. */
  const handleSyncStorage = () => {
    syncDestinations.mutate(undefined, {
      onSuccess: (response) => {
        const { imported, alreadyTracked, errors, configuration } =
          response.data;
        const recovered =
          configuration && configuration.destinationsImported > 0
            ? ` · restored ${configuration.destinationsImported} destination configuration(s)`
            : configuration?.scheduleImported
              ? ' · restored the backup schedule'
              : '';
        if (imported > 0) {
          toast.success(
            `Imported ${imported} backup${imported === 1 ? '' : 's'} from storage${recovered}`,
          );
        } else if (recovered) {
          toast.success(`Storage synced${recovered}`);
        } else {
          toast.info(
            alreadyTracked > 0
              ? 'All stored backups are already listed'
              : 'No backups found in the configured destinations',
          );
        }
        if (errors.length > 0) {
          toast.error(`${errors[0].destination}: ${errors[0].message}`);
        }
      },
      onError: (err: any) =>
        toast.error(err?.message || 'Could not scan the destinations'),
    });
  };

  const handleDownload = async (record: BackupRecord) => {
    try {
      await backupService.downloadBackup(record);
    } catch (err: any) {
      toast.error(err?.message || 'Download failed');
    }
  };

  /** Opens the restore dialog with fresh defaults for the chosen archive. */
  const openRestore = (record: BackupRecord) => {
    setDetailsRecord(null);
    setRestoreRecord(record);
    // Never default the database off for an archive that contains tables.
    setRestoreDb(record.includeDatabase !== false || record.tableCount > 0);
    setRestoreFiles(record.includeAttachments);
    setRestoreModules([]);
    setRestoreConfirm('');
    setRestoreMode('merge');
    setRestorePreview(null);
  };

  const restoreKeyword =
    restoreMode === 'clean' ? CLEAN_RESTORE_KEYWORD : RESTORE_KEYWORD;

  const handleRestore = () => {
    if (!restoreRecord) return;
    if (restoreConfirm.trim().toUpperCase() !== restoreKeyword) {
      toast.error(`Type ${restoreKeyword} to confirm`);
      return;
    }
    restoreBackup.mutate(
      {
        id: restoreRecord.id,
        payload: buildRestorePayload({
          includeDatabase: restoreDb,
          includeAttachments: restoreFiles,
          safetyBackup: restoreSafety,
          modules: restoreModules,
          mode: restoreMode,
          confirmText: restoreConfirm,
        }),
      },
      {
        onSuccess: (response) => {
          const { jobId } = response.data;
          setRestoreJobId(jobId);
          setRestoreRecord(null);
          setRestoreConfirm('');
          toast.info(
            restoreMode === 'clean'
              ? 'Clean restore queued — all existing data will be replaced'
              : 'Restore queued — running in the background',
          );
        },
        onError: (err: any) => toast.error(err?.message || 'Restore failed'),
      },
    );
  };

  /** Read-only preview for the selected archive (no data is touched). */
  const runRestorePreview = () => {
    if (!restoreRecord) return;
    previewRestore.mutate(
      {
        id: restoreRecord.id,
        payload: buildRestorePayload({
          includeDatabase: restoreDb,
          includeAttachments: restoreFiles,
          safetyBackup: restoreSafety,
          modules: restoreModules,
          mode: restoreMode,
          confirmText: restoreConfirm,
        }),
      },
      {
        onSuccess: (response) => setRestorePreview(response.data),
        onError: (err: any) =>
          toast.error(err?.message || 'Could not preview the restore'),
      },
    );
  };

  const resetUpload = () => {
    setUploadInfo(null);
    setUploadDb(true);
    setUploadFiles(true);
    setUploadSafety(true);
    setUploadModules([]);
    setUploadConfirm('');
    setUploadMode('merge');
    setUploadPreview(null);
    inspectUpload.reset();
  };

  const openUploadRestore = () => {
    resetUpload();
    setUploadOpen(true);
  };

  const closeUploadRestore = () => {
    setUploadOpen(false);
    resetUpload();
  };

  const handleUploadFile = (file: File | null) => {
    if (!file) return;
    setUploadInfo(null);
    setUploadConfirm('');
    setUploadModules([]);
    setUploadPreview(null);
    inspectUpload.mutate(file, {
      onSuccess: (response) => {
        const info = response.data;
        setUploadInfo(info);
        setUploadDb(info.includeDatabase);
        setUploadFiles(info.includeAttachments);
        toast.success(`Loaded ${info.filename}`);
      },
      onError: (err: any) =>
        toast.error(err?.message || 'Could not read the backup file'),
    });
  };

  const uploadKeyword =
    uploadMode === 'clean' ? CLEAN_RESTORE_KEYWORD : RESTORE_KEYWORD;

  const handleUploadRestore = () => {
    if (!uploadInfo) return;
    if (uploadConfirm.trim().toUpperCase() !== uploadKeyword) {
      toast.error(`Type ${uploadKeyword} to confirm`);
      return;
    }
    restoreUpload.mutate(
      {
        token: uploadInfo.token,
        ...buildRestorePayload({
          includeDatabase: uploadDb,
          includeAttachments: uploadFiles,
          safetyBackup: uploadSafety,
          modules: uploadModules,
          mode: uploadMode,
          confirmText: uploadConfirm,
        }),
      },
      {
        onSuccess: (response) => {
          const { jobId } = response.data;
          setRestoreJobId(jobId);
          closeUploadRestore();
          toast.info(
            uploadMode === 'clean'
              ? 'Clean restore queued — all existing data will be replaced'
              : 'Restore queued — running in the background',
          );
        },
        onError: (err: any) => toast.error(err?.message || 'Restore failed'),
      },
    );
  };

  /** Read-only preview for the uploaded archive. */
  const runUploadPreview = () => {
    if (!uploadInfo) return;
    previewUpload.mutate(
      {
        token: uploadInfo.token,
        ...buildRestorePayload({
          includeDatabase: uploadDb,
          includeAttachments: uploadFiles,
          safetyBackup: uploadSafety,
          modules: uploadModules,
          mode: uploadMode,
          confirmText: uploadConfirm,
        }),
      },
      {
        onSuccess: (response) => setUploadPreview(response.data),
        onError: (err: any) =>
          toast.error(err?.message || 'Could not preview the restore'),
      },
    );
  };

  const activeProviderMeta = configProvider
    ? PROVIDER_META[configProvider]
    : null;

  const oauthInfo = configProvider
    ? oauthStatus.data?.data?.[configProvider]
    : undefined;
  const appManaged = Boolean(oauthInfo?.appConfigured);
  const visibleFields = configProvider
    ? PROVIDER_FIELDS[configProvider].filter(
        (field) => !field.credential || !appManaged || showAdvanced,
      )
    : [];

  return (
    <div className="space-y-4">
      {/* ───────────────────────────── Header ───────────────────────────── */}
      <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold tracking-tight text-text">
                  Backup &amp; Restore
                </h1>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6875rem] font-medium',
                    health?.ok === false
                      ? 'bg-danger/10 text-danger'
                      : 'bg-success/10 text-success',
                  )}
                  title={
                    health
                      ? `${health.provider} · ${health.bucket}${
                          health.error ? ' · ' + health.error : ''
                        }`
                      : 'Object storage (attachments)'
                  }
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      health?.ok === false ? 'bg-danger' : 'bg-success',
                    )}
                  />
                  Storage {health ? (health.ok ? 'OK' : 'error') : '…'}
                </span>
                <Badge
                  variant={queue?.isPaused ? 'warning' : 'default'}
                  title={
                    queue
                      ? `Waiting ${queue.waiting} · Active ${queue.active} · Completed ${queue.completed} · Failed ${queue.failed}`
                      : 'Backup queue'
                  }
                >
                  Queue {queue?.isPaused ? 'paused' : `${queue?.active ?? 0} active`}
                </Badge>
              </div>
              <p className="mt-0.5 max-w-3xl text-xs leading-relaxed text-text-muted">
                Portable <code className="font-mono">.hmsbkup</code> archives of
                the database and attachments — compressed, encrypted when a key
                is configured, and built in the background. Pause, resume or
                cancel any run; restore a whole archive or just the modules you
                need.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => overview.refetch()}
              title="Refresh the backup overview"
            >
              <RefreshCw size={15} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                queue?.isPaused ? resumeQueue.mutate() : pauseQueue.mutate()
              }
              title="Pause or resume the backup queue"
            >
              {queue?.isPaused ? (
                <Play size={14} className="mr-1.5" />
              ) : (
                <Pause size={14} className="mr-1.5" />
              )}
              {queue?.isPaused ? 'Resume queue' : 'Pause queue'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncStorage}
              disabled={syncDestinations.isPending}
              title="Look for archives already stored in the configured destinations"
            >
              {syncDestinations.isPending ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <CloudDownload size={14} className="mr-1.5" />
              )}
              Sync storage
            </Button>
            <Button variant="outline" size="sm" onClick={openUploadRestore}>
              <Upload size={14} className="mr-1.5" />
              Restore file
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus size={14} className="mr-1.5" />
              New backup
            </Button>
          </div>
        </div>

        {/* Compact KPI strip */}
        <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3 xl:grid-cols-5">
          <Kpi
            icon={FileArchive}
            label="Archives"
            value={stats?.total ?? 0}
            hint={`${stats?.completed ?? 0} completed`}
          />
          <Kpi
            icon={AlertCircle}
            label="Failed"
            value={stats?.failed ?? 0}
            hint={stats?.failed ? 'Check the history' : 'All healthy'}
            tone={stats?.failed ? 'danger' : undefined}
          />
          <Kpi
            icon={Layers}
            label="Data tables"
            value={dataTableCount}
            hint="Included in a full backup"
          />
          <Kpi
            icon={Server}
            label="Destinations"
            value={`${stats?.destinationsConfigured ?? 0}/${destinationList.length}`}
            hint="enabled / configured"
          />
          <Kpi
            icon={CalendarClock}
            label="Next run"
            value={
              scheduleData?.isEnabled
                ? formatDateTime(scheduleData.nextRunAt || undefined)
                : 'Manual'
            }
            hint={
              scheduleData?.isEnabled
                ? `${scheduleData.frequency} · ${scheduleData.timezone}`
                : 'Schedule disabled'
            }
            small
          />
        </div>
      </div>

      {/* Live backup run (async queue) */}
      {activeRunId && (
        <ActiveRunCard
          title="Backup run"
          subtitle={run.data?.data?.record?.filename}
          run={run.data?.data}
          isLoading={run.isLoading}
          busy={
            pauseBackup.isPending ||
            resumeBackup.isPending ||
            cancelBackup.isPending ||
            destroyJob.isPending
          }
          onClose={() => setActiveRunId(null)}
          onPause={() =>
            pauseBackup.mutate(activeRunId, {
              onSuccess: () => toast.info('Backup paused'),
            })
          }
          onResume={() =>
            resumeBackup.mutate(activeRunId, {
              onSuccess: () => toast.info('Backup resumed'),
            })
          }
          onCancel={() =>
            cancelBackup.mutate(activeRunId, {
              onSuccess: () => toast.success('Backup cancelled'),
            })
          }
          onDestroy={() =>
            destroyJob.mutate(activeRunId, {
              onSuccess: () => {
                toast.success('Backup job removed from the queue');
                setActiveRunId(null);
              },
            })
          }
          onRetry={() =>
            retryBackup.mutate(activeRunId, {
              onSuccess: () => toast.info('Retrying backup'),
              onError: (err: any) =>
                toast.error(err?.message || 'Could not retry the backup'),
            })
          }
        />
      )}

      {/* Live restore run (async queue) */}
      {restoreJobId && (
        <ActiveRunCard
          title="Restore run"
          subtitle="Restoring from backup"
          run={restoreRun.data?.data}
          isLoading={restoreRun.isLoading}
          busy={
            pauseRestore.isPending ||
            resumeRestore.isPending ||
            cancelRestore.isPending ||
            destroyRestoreJob.isPending
          }
          onClose={() => setRestoreJobId(null)}
          onPause={() =>
            pauseRestore.mutate(restoreJobId, {
              onSuccess: () => toast.info('Restore paused'),
            })
          }
          onResume={() =>
            resumeRestore.mutate(restoreJobId, {
              onSuccess: () => toast.info('Restore resumed'),
            })
          }
          onCancel={() =>
            cancelRestore.mutate(restoreJobId, {
              onSuccess: () => toast.success('Restore cancelled'),
            })
          }
          onDestroy={() =>
            destroyRestoreJob.mutate(restoreJobId, {
              onSuccess: () => {
                toast.success('Restore job removed from the queue');
                setRestoreJobId(null);
              },
            })
          }
          onRetry={() =>
            retryRestore.mutate(restoreJobId, {
              onSuccess: () => toast.info('Retrying restore'),
              onError: (err: any) =>
                toast.error(err?.message || 'Could not retry the restore'),
            })
          }
        />
      )}

      {/* Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedTabs
          value={tab}
          onChange={setTab}
          tabs={[
            {
              key: 'destinations',
              label: 'Destinations',
              icon: Server,
              badge: enabledDestinations || undefined,
            },
            {
              key: 'schedule',
              label: 'Schedule',
              icon: CalendarClock,
              dot: scheduleData?.isEnabled,
            },
            {
              key: 'history',
              label: 'Archives',
              icon: FileArchive,
              badge: stats?.total || undefined,
            },
          ]}
        />
        <p className="text-[0.6875rem] text-text-muted">
          {stats?.lastBackupAt
            ? `Last backup ${formatDateTime(stats.lastBackupAt)}`
            : 'No backup has been created yet'}
        </p>
      </div>

      {tab === 'destinations' && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {destinations.isLoading &&
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="flex h-28 items-center justify-center rounded-xl border border-border bg-surface"
              >
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ))}

          {destinationList.map((destination) => (
            <DestinationCard
              key={destination.provider}
              destination={destination}
              onConfigure={() => openConfig(destination)}
              onTest={() => handleTest(destination.provider)}
              onConnect={() => handleConnect(destination.provider)}
              onToggle={(patch) => handleToggle(destination, patch)}
              onRemove={() => setDeleteProvider(destination.provider)}
              busy={testDestination.isPending || saveDestination.isPending}
            />
          ))}

          {localFolderSupported && (
            <div className="rounded-xl border border-dashed border-border bg-surface/60 p-3.5 text-xs text-text-muted lg:col-span-2">
              <div className="flex items-start gap-2">
                <Info size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>
                  Pick <strong>Local / Server → Configure</strong> to also write
                  a copy of completed backups straight into a folder on this PC.
                  Automatic backups always land on the server; a PC folder is
                  only written while this page is open.
                </span>
              </div>
            </div>
          )}

          {/* Encryption key health (F5). The key itself never leaves the server. */}
          <div className="rounded-xl border border-border bg-surface p-3.5 shadow-sm lg:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex items-start gap-2">
                <ShieldCheck
                  size={15}
                  className={cn(
                    'mt-0.5 shrink-0',
                    encryption.data?.data?.enabled
                      ? encryption.data?.data?.strong
                        ? 'text-success'
                        : 'text-warning'
                      : 'text-text-muted',
                  )}
                />
                <div>
                  <h3 className="text-sm font-semibold text-text">
                    Archive encryption
                  </h3>
                  <p className="text-[0.6875rem] text-text-muted">
                    {encryption.isLoading
                      ? 'Checking the backup key…'
                      : encryption.data?.data?.message ||
                        'Encryption status unavailable.'}
                  </p>
                </div>
              </div>
              {encryption.data?.data?.fingerprint && (
                <code className="rounded bg-background px-1.5 py-0.5 text-[0.6875rem] text-text-muted">
                  key {encryption.data.data.fingerprint}
                </code>
              )}
            </div>

            {encryption.data?.data && (
              <dl className="mt-2 grid grid-cols-2 gap-1.5 border-t border-border pt-2 text-[0.6875rem] sm:grid-cols-4">
                <div>
                  <dt className="text-text-muted">Configured</dt>
                  <dd className="text-text">
                    {encryption.data.data.configured ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-muted">Key length</dt>
                  <dd className="text-text">
                    {encryption.data.data.keyLength} /{' '}
                    {encryption.data.data.minimumLength}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-muted">Strength</dt>
                  <dd
                    className={cn(
                      encryption.data.data.strong
                        ? 'text-success'
                        : 'text-warning',
                    )}
                  >
                    {encryption.data.data.strong ? 'Strong' : 'Weak'}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-muted">New archives</dt>
                  <dd className="text-text">
                    {encryption.data.data.enabled ? 'Encrypted' : 'Plaintext'}
                  </dd>
                </div>
              </dl>
            )}

            <p className="mt-2 text-[0.6875rem] text-text-muted">
              Set <code>BACKUP_ENCRYPTION_KEY</code> in the server environment to
              encrypt new archives. After rotating the key, use{' '}
              <strong>Re-encrypt</strong> on an archive in the Archives tab to
              migrate it to the new key.
            </p>
          </div>
        </div>
      )}

      {tab === 'schedule' && (
        <SchedulePanel
          schedule={scheduleData}
          loading={schedule.isLoading}
          destinations={destinationList}
          modules={moduleList}
          saving={updateSchedule.isPending}
          running={runScheduleNow.isPending}
          onSave={(payload) =>
            updateSchedule.mutate(payload, {
              onSuccess: () => toast.success('Schedule saved'),
              onError: (err: any) =>
                toast.error(err?.message || 'Could not save schedule'),
            })
          }
          onRunNow={() => {
            if (!scheduleData) return;
            runScheduleNow.mutate(undefined, {
              onSuccess: (response) => {
                toast.success('Scheduled backup queued');
                setActiveRunId(response.data.record.id);
                setTab('history');
              },
              onError: (err: any) =>
                toast.error(
                  err?.message || 'Could not start the scheduled backup',
                ),
            });
          }}
        />
      )}

      {tab === 'history' && (
        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
              {selectedIds.length > 0 ? (
                <>
                  <span className="font-medium text-text">
                    {selectedIds.length} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedIds([])}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setBulkDeleteOpen(true)}
                    disabled={deleteBackups.isPending}
                  >
                    {deleteBackups.isPending ? (
                      <Loader2 size={13} className="mr-1 animate-spin" />
                    ) : (
                      <Trash2 size={13} className="mr-1" />
                    )}
                    Delete
                  </Button>
                </>
              ) : (
                <span>
                  {totalRecords} archive{totalRecords === 1 ? '' : 's'}
                  {statusFilter ? ` · filtered` : ''}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <select
                aria-label="Filter archives by status"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                className="rounded-lg border border-border bg-surface px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="running">Running</option>
                <option value="queued">Queued</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                aria-label="Rows per page"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-border bg-surface px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncStorage}
                disabled={syncDestinations.isPending}
                title="Scan the configured destinations for archives this server does not know about"
              >
                {syncDestinations.isPending ? (
                  <Loader2 size={13} className="mr-1 animate-spin" />
                ) : (
                  <CloudDownload size={13} className="mr-1" />
                )}
                Sync
              </Button>
            </div>
          </div>

          {backups.isLoading ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
              <FileArchive className="h-9 w-9 text-text-muted" />
              <p className="text-sm text-text-muted">
                {statusFilter
                  ? 'No archives match this status'
                  : totalRecords > 0
                    ? 'No backups on this page'
                    : 'No backups yet'}
              </p>
              {statusFilter ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStatusFilter('')}
                >
                  Clear filter
                </Button>
              ) : totalRecords > 0 ? (
                <Button variant="outline" size="sm" onClick={() => setPage(1)}>
                  Back to the first page
                </Button>
              ) : (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncStorage}
                    disabled={syncDestinations.isPending}
                  >
                    <CloudDownload size={13} className="mr-1" />
                    Sync storage
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateOpen(true)}
                  >
                    <Plus size={13} className="mr-1" />
                    Create your first backup
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-surface">
                  <tr className="border-b border-border text-left text-[0.625rem] uppercase tracking-wider text-text-muted">
                    <th className="w-9 py-2 pl-3 pr-1 font-semibold">
                      <input
                        type="checkbox"
                        aria-label="Select all backups on this page"
                        checked={allOnPageSelected}
                        ref={(element) => {
                          if (element) {
                            element.indeterminate = someOnPageSelected;
                          }
                        }}
                        onChange={toggleSelectAll}
                        className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary"
                      />
                    </th>
                    <th className="py-2 pr-3 font-semibold">Archive</th>
                    <th className="px-3 py-2 font-semibold">Contents</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 font-semibold">Created</th>
                    <th className="py-2 pl-3 pr-3 text-right font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => {
                    const badge = STATUS_BADGE[record.status];
                    const selected = selectedIds.includes(record.id);
                    const meta = record.metadata || {};
                    const rowModules: string[] = Array.isArray(meta.modules)
                      ? (meta.modules as string[])
                      : [];
                    const moduleLabels = rowModules.map(
                      (key) =>
                        moduleList.find((module) => module.key === key)?.label ||
                        key,
                    );
                    return (
                      <tr
                        key={record.id}
                        className={cn(
                          'border-b border-border/60 align-top transition-colors last:border-0',
                          selected ? 'bg-primary/5' : 'hover:bg-background/70',
                        )}
                      >
                        <td className="py-2.5 pl-3 pr-1">
                          <input
                            type="checkbox"
                            aria-label={`Select ${record.filename}`}
                            checked={selected}
                            onChange={() => toggleSelect(record.id)}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary"
                          />
                        </td>
                        <td className="max-w-[22rem] py-2.5 pr-3">
                          <button
                            type="button"
                            onClick={() => setDetailsRecord(record)}
                            className="group/name flex items-start gap-1 text-left text-[0.8125rem] font-medium text-text hover:text-primary"
                            title="View backup details"
                          >
                            <span className="break-all underline-offset-2 group-hover/name:underline">
                              {record.filename}
                            </span>
                            <Eye
                              size={13}
                              className="mt-0.5 shrink-0 text-text-muted opacity-0 transition-opacity group-hover/name:opacity-100"
                            />
                          </button>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[0.6875rem] text-text-muted">
                            <span>
                              {record.trigger === 'scheduled'
                                ? 'Scheduled'
                                : 'Manual'}
                            </span>
                            <span>·</span>
                            <span>
                              {PROVIDER_META[record.provider]?.label ||
                                record.provider}
                            </span>
                            {record.createdByName && (
                              <>
                                <span>·</span>
                                <span className="max-w-[10rem] truncate">
                                  {record.createdByName}
                                </span>
                              </>
                            )}
                            <span>·</span>
                            <span>{formatBytes(record.sizeBytes)}</span>
                          </div>
                          {moduleLabels.length > 0 && (
                            <div
                              className="mt-0.5 max-w-[20rem] truncate text-[0.6875rem] text-text-muted"
                              title={moduleLabels.join(', ')}
                            >
                              {moduleLabels.slice(0, 3).join(', ')}
                              {moduleLabels.length > 3
                                ? ` +${moduleLabels.length - 3}`
                                : ''}
                            </div>
                          )}
                          {record.errorMessage && (
                            <div className="mt-0.5 line-clamp-2 text-[0.6875rem] text-danger">
                              {record.errorMessage}
                            </div>
                          )}
                          {meta.attachmentWarning && (
                            <div className="mt-0.5 line-clamp-2 text-[0.6875rem] text-warning">
                              {String(meta.attachmentWarning)}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-[0.6875rem] text-text-muted">
                          {record.includeDatabase ? (
                            <span>
                              {record.tableCount} table(s)
                              {typeof meta.rowCount === 'number'
                                ? ` · ${Number(
                                    meta.rowCount,
                                  ).toLocaleString()} row(s)`
                                : ''}
                            </span>
                          ) : (
                            <span>No database</span>
                          )}
                          <span className="block">
                            {record.includeAttachments
                              ? `${record.attachmentCount} file(s)`
                              : 'No attachments'}
                          </span>
                          {record.includeDatabase && meta.rowCount === 0 && (
                            <span className="mt-0.5 block font-medium text-warning">
                              Empty archive
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                            {meta.partial && (
                              <Badge variant="warning">Partial</Badge>
                            )}
                            {meta.encrypted && (
                              <Badge variant="success" title="AES-256-GCM">
                                Encrypted
                              </Badge>
                            )}
                            {meta.imported && (
                              <Badge
                                variant="default"
                                title="Imported from storage — created by another installation"
                              >
                                Imported
                              </Badge>
                            )}
                            {meta.manifestUnread && (
                              <Badge
                                variant="warning"
                                title="The archive was found in storage, but its manifest could not be read on this server. It may be encrypted with a different key."
                              >
                                Metadata unavailable
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-[0.6875rem] text-text-muted">
                          {formatDateTime(
                            meta.imported
                              ? record.completedAt || record.createdAt
                              : record.createdAt,
                          )}
                        </td>
                        <td className="py-2 pl-3 pr-3">
                          <div className="flex items-center justify-end gap-0.5">
                            {localFolderName && (
                              <IconAction
                                title="Save to this PC"
                                disabled={
                                  record.status !== 'completed' ||
                                  savingToPcId === record.id
                                }
                                onClick={() => saveRecordToPc(record)}
                              >
                                {savingToPcId === record.id ? (
                                  <Loader2 size={15} className="animate-spin" />
                                ) : (
                                  <HardDriveDownload size={15} />
                                )}
                              </IconAction>
                            )}
                            <IconAction
                              title="Download"
                              disabled={record.status !== 'completed'}
                              onClick={() => handleDownload(record)}
                            >
                              <Download size={15} />
                            </IconAction>
                            <IconAction
                              title="Verify integrity"
                              disabled={record.status !== 'completed'}
                              onClick={() => handleVerify(record)}
                            >
                              <ShieldCheck size={15} />
                            </IconAction>
                            <IconAction
                              title="Restore this archive"
                              disabled={record.status !== 'completed'}
                              onClick={() => openRestore(record)}
                            >
                              <RotateCcw size={15} />
                            </IconAction>
                            <IconAction
                              title="Delete"
                              danger
                              onClick={() => setDeleteBackupId(record.id)}
                            >
                              <Trash2 size={15} />
                            </IconAction>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalRecords > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3.5 py-2">
              <span className="text-[0.6875rem] text-text-muted">
                Showing {pageStart}–{pageEnd} of {totalRecords}
                {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
              </span>
              {totalPages > 1 && (
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  {paginationWindow(page, totalPages).map((entry, index) =>
                    entry === 'gap' ? (
                      <span
                        key={`gap-${index}`}
                        className="px-1 text-xs text-text-muted"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={entry}
                        type="button"
                        onClick={() => setPage(entry)}
                        aria-current={entry === page ? 'page' : undefined}
                        className={cn(
                          'min-w-7 rounded-lg border px-2 py-1 text-xs font-medium transition-colors',
                          entry === page
                            ? 'border-primary bg-primary text-white'
                            : 'border-border bg-surface text-text-muted hover:bg-background hover:text-text',
                        )}
                      >
                        {entry}
                      </button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create backup modal */}
      {createOpen && (
        <Modal title="Create backup" onClose={closeCreate} icon={Plus}>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-text">
                Destination
              </label>
              <select
                value={createDest}
                onChange={(e) => setCreateDest(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Active destination (default)</option>
                {destinationList
                  .filter((d) => d.isEnabled)
                  .map((d) => (
                    <option key={d.provider} value={d.id}>
                      {d.label}
                      {d.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-[0.6875rem] text-text-muted">
                The archive is built on the server, then uploaded to the chosen
                storage.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/50 p-3 sm:grid-cols-2">
              <Checkbox
                checked={createDb}
                onChange={setCreateDb}
                label="Include database"
              />
              <Checkbox
                checked={createFiles}
                onChange={setCreateFiles}
                label="Include attachments"
              />
            </div>

            {createDb && (
              <ModuleSelector
                modules={moduleList}
                selected={createModules}
                onChange={setCreateModules}
                loading={modules.isLoading}
              />
            )}

            {localFolderName && (
              <Checkbox
                checked={saveToPc}
                onChange={setSaveToPc}
                label={`Also save a copy to "${localFolderName}" on this PC`}
                hint="The archive is downloaded from the server into that folder once it is created. Keep this page open until it finishes."
              />
            )}

            {!createDb && !createFiles && (
              <p className="text-xs text-danger">
                Select at least one item to include.
              </p>
            )}

            <ModalFooter onCancel={closeCreate}>
              <Button
                onClick={handleCreate}
                disabled={
                  createBackup.isPending || (!createDb && !createFiles)
                }
                isLoading={createBackup.isPending}
              >
                <Play size={15} className="mr-1.5" />
                Create now
              </Button>
            </ModalFooter>
          </div>
        </Modal>
      )}

      {/* Destination config modal */}
      {configProvider && activeProviderMeta && (
        <Modal
          title={`Configure ${activeProviderMeta.label}`}
          onClose={closeConfig}
          wide
          icon={activeProviderMeta.icon}
        >
          <div className="space-y-4">
            <p className="text-xs text-text-muted">
              {activeProviderMeta.description}
            </p>

            {visibleFields.map((field) => {
              if (field.type === 'boolean') {
                return (
                  <Checkbox
                    key={field.key}
                    checked={Boolean(configForm[field.key])}
                    onChange={(value) =>
                      setConfigForm((prev) => ({ ...prev, [field.key]: value }))
                    }
                    label={field.label}
                    hint={field.hint}
                  />
                );
              }
              if (field.type === 'url') {
                const rawUrl = String(configForm[field.key] ?? '');
                const hostAndPath = rawUrl.replace(/^https?:\/\//i, '');
                return (
                  <div key={field.key}>
                    <label className="mb-1 block text-xs font-medium text-text">
                      {field.label}
                    </label>
                    <div className="flex gap-2">
                      <select
                        aria-label="URL protocol"
                        value={configUrlScheme}
                        onChange={(event) => {
                          const next = event.target.value as 'http' | 'https';
                          setConfigUrlScheme(next);
                          setConfigForm((prev) => ({
                            ...prev,
                            [field.key]: hostAndPath
                              ? `${next}://${hostAndPath}`
                              : '',
                          }));
                        }}
                        className="shrink-0 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="https">https://</option>
                        <option value="http">http://</option>
                      </select>
                      <Input
                        value={hostAndPath}
                        placeholder={field.placeholder}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const pastedScheme = /^http:\/\//i.test(raw)
                            ? 'http'
                            : /^https:\/\//i.test(raw)
                              ? 'https'
                              : configUrlScheme;
                          if (pastedScheme !== configUrlScheme) {
                            setConfigUrlScheme(pastedScheme);
                          }
                          const host = raw.replace(/^https?:\/\//i, '');
                          setConfigForm((prev) => ({
                            ...prev,
                            [field.key]: host
                              ? `${pastedScheme}://${host}`
                              : '',
                          }));
                        }}
                      />
                    </div>
                    {field.hint && (
                      <p className="mt-1 text-[0.6875rem] leading-relaxed text-text-muted">
                        {field.hint}
                      </p>
                    )}
                  </div>
                );
              }
              return (
                <div key={field.key}>
                  <Input
                    label={field.label}
                    type={field.secret ? 'password' : 'text'}
                    placeholder={
                      field.secret && configForm[field.key]
                        ? 'Leave blank to keep the current value'
                        : field.placeholder
                    }
                    value={configForm[field.key] ?? ''}
                    onChange={(e) =>
                      setConfigForm((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                  />
                  {field.hint && (
                    <p className="mt-1 text-[0.6875rem] leading-relaxed text-text-muted">
                      {field.hint}
                    </p>
                  )}
                </div>
              );
            })}

            {configProvider === 'local' && (
              <div className="space-y-3 rounded-xl border border-border bg-background/60 p-3">
                <div className="flex items-start gap-2">
                  <FolderOpen size={15} className="mt-0.5 shrink-0 text-primary" />
                  <div className="space-y-0.5">
                    <p className="text-xs font-medium text-text">
                      Save to a folder on this PC
                    </p>
                    <p className="text-[0.6875rem] leading-relaxed text-text-muted">
                      Optional. Pick a folder on this computer and completed
                      backups you trigger here are also written straight to it.
                      Nothing leaves your PC — the file is downloaded from the
                      server directly into the folder.
                    </p>
                  </div>
                </div>

                {!localFolderSupported ? (
                  <p className="text-[0.6875rem] text-warning">
                    This browser can&apos;t write to a local folder. Use Chrome
                    or Edge, or download archives from the list instead.
                  </p>
                ) : localFolderName ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[0.6875rem] text-text">
                      Folder:{' '}
                      <code className="break-all">{localFolderName}</code>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePickLocalFolder}
                    >
                      Change
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      onClick={handleClearLocalFolder}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePickLocalFolder}
                  >
                    <FolderOpen size={13} className="mr-1" />
                    Select folder on this PC
                  </Button>
                )}

                <p className="text-[0.625rem] text-text-muted">
                  Automatic/scheduled backups still land on the server — a PC
                  folder can only be written while this page is open.
                </p>
              </div>
            )}

            {configProvider === 'google_drive' && configForm.accountEmail && (
              <p className="text-[0.6875rem] text-success">
                Connected as {configForm.accountEmail}
              </p>
            )}

            {activeProviderMeta.oauth && (
              <div className="space-y-2">
                {appManaged && !showAdvanced ? (
                  <div className="rounded-xl border border-success/30 bg-success/10 p-2.5 text-[0.6875rem] text-success">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                      <div className="space-y-1">
                        <p>
                          Ready to connect — no credentials needed. This app
                          manages the {activeProviderMeta.label} integration.
                        </p>
                        <p className="break-all opacity-90">
                          Redirect URI: <code>{oauthInfo?.redirectUri}</code>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 rounded-xl border border-warning/30 bg-warning/10 p-2.5 text-[0.6875rem] text-warning">
                    <p>
                      {appManaged
                        ? 'Using a custom OAuth app. The credentials below override the built-in app.'
                        : `This app has no built-in ${activeProviderMeta.label} credentials yet. Enter your own OAuth app credentials below, or ask your administrator to set the app-level environment variables.`}
                    </p>
                    <p className="break-all">
                      Redirect URI to register:{' '}
                      <code>{oauthInfo?.redirectUri}</code>
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleConnect(configProvider)}
                  >
                    <Link2 size={13} className="mr-1.5" />
                    Connect {activeProviderMeta.label}
                  </Button>
                  {appManaged && (
                    <button
                      type="button"
                      onClick={() => setShowAdvanced((value) => !value)}
                      className="text-[0.6875rem] text-primary hover:underline"
                    >
                      {showAdvanced
                        ? 'Hide custom OAuth app settings'
                        : 'Use a custom OAuth app instead'}
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/50 p-3 sm:grid-cols-2">
              <Checkbox
                checked={configEnabled}
                onChange={setConfigEnabled}
                label="Enable this destination"
              />
              <Checkbox
                checked={configDefault}
                onChange={setConfigDefault}
                label="Default for scheduled backups"
              />
            </div>

            {testResult && (
              <div
                className={cn(
                  'flex items-start gap-2 rounded-xl p-2.5 text-xs',
                  testResult.ok
                    ? 'bg-success/10 text-success'
                    : 'bg-danger/10 text-danger',
                )}
              >
                {testResult.ok ? (
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                )}
                <span>{testResult.message}</span>
              </div>
            )}

            <ModalFooter onCancel={closeConfig}>
              <Button
                variant="outline"
                onClick={() => handleTest(configProvider, configForm)}
                disabled={testDestination.isPending}
                isLoading={testDestination.isPending}
              >
                <RefreshCw size={15} className="mr-1.5" />
                Test connection
              </Button>
              <Button
                onClick={handleSaveDestination}
                isLoading={saveDestination.isPending}
              >
                <Save size={15} className="mr-1.5" />
                Save
              </Button>
            </ModalFooter>
          </div>
        </Modal>
      )}

      {/* Restore modal */}
      {restoreRecord && (
        <Modal
          title="Restore from backup"
          onClose={() => setRestoreRecord(null)}
          wide
          icon={RotateCcw}
        >
          <div className="space-y-4">
            <div className="rounded-xl border border-danger/30 bg-danger/10 p-2.5 text-xs text-danger">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Restoring replaces current data with{' '}
                  <strong className="break-all">{restoreRecord.filename}</strong>.
                  This cannot be undone.
                </span>
              </div>
            </div>

            <ArchiveSummary
              tables={restoreRecord.tableCount}
              rows={
                typeof restoreRecord.metadata?.rowCount === 'number'
                  ? Number(restoreRecord.metadata.rowCount)
                  : null
              }
              files={restoreRecord.attachmentCount}
              includeDatabase={restoreRecord.includeDatabase}
              includeAttachments={restoreRecord.includeAttachments}
            />

            {restoreDb &&
              restoreRecord.includeDatabase &&
              restoreRecord.metadata?.rowCount === 0 && (
                <div className="flex items-start gap-2 rounded-xl bg-warning/10 p-2.5 text-xs text-warning">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    This archive contains <strong>no data rows</strong> — it was
                    created from an empty database. Restoring it will leave the
                    selected modules empty. Pick a backup taken from the
                    installation that holds the real data.
                  </span>
                </div>
              )}

            <div className="grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/50 p-3 sm:grid-cols-3">
              <Checkbox
                checked={restoreDb}
                onChange={(value) => {
                  setRestoreDb(value);
                  setRestorePreview(null);
                }}
                label="Database"
              />
              <Checkbox
                checked={restoreFiles}
                onChange={(value) => {
                  setRestoreFiles(value);
                  setRestorePreview(null);
                }}
                label="Attachments"
              />
              <Checkbox
                checked={restoreMode === 'clean' ? true : restoreSafety}
                onChange={(value) => {
                  if (restoreMode !== 'clean') setRestoreSafety(value);
                }}
                label="Safety backup"
                hint={
                  restoreMode === 'clean'
                    ? 'Always taken before a clean restore'
                    : undefined
                }
              />
            </div>

            {!restoreDb &&
              (restoreRecord.includeDatabase || restoreRecord.tableCount > 0) && (
                <div className="flex items-start gap-2 rounded-xl bg-warning/10 p-2.5 text-xs text-warning">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    <strong>Restore database is turned off.</strong> Only
                    attachments will be restored — patient, receipt and billing
                    data in this archive will be skipped.
                  </span>
                </div>
              )}

            <RestoreModePicker
              value={restoreMode}
              onChange={(mode) => {
                setRestoreMode(mode);
                setRestoreConfirm('');
                setRestorePreview(null);
              }}
              tables={Math.max(dataTableCount, restoreRecord.tableCount)}
              attachments={restoreFiles && restoreRecord.includeAttachments}
            />

            {restoreDb &&
              (restoreMode === 'merge' || restoreMode === 'skip') && (
                <ModuleSelector
                  modules={modulesAvailableInRecord(moduleList, restoreRecord)}
                  selected={restoreModules}
                  onChange={(next) => {
                    setRestoreModules(next);
                    setRestorePreview(null);
                  }}
                  loading={modules.isLoading}
                />
              )}

            <RestorePreviewCard
              preview={restorePreview}
              loading={previewRestore.isPending}
              onPreview={runRestorePreview}
              disabled={!restoreDb && !restoreFiles}
            />

            <RestoreConfirmInput
              value={restoreConfirm}
              onChange={setRestoreConfirm}
              keyword={restoreKeyword}
              danger={restoreMode === 'clean'}
            />

            <ModalFooter onCancel={() => setRestoreRecord(null)}>
              <Button
                variant="danger"
                onClick={handleRestore}
                isLoading={restoreBackup.isPending}
                disabled={
                  restoreConfirm.trim().toUpperCase() !== restoreKeyword ||
                  (!restoreDb && !restoreFiles)
                }
              >
                {restoreMode === 'clean' ? (
                  <Eraser size={15} className="mr-1.5" />
                ) : (
                  <RotateCcw size={15} className="mr-1.5" />
                )}
                {restoreMode === 'clean' ? 'Wipe and restore' : 'Restore now'}
              </Button>
            </ModalFooter>
          </div>
        </Modal>
      )}

      {/* Upload-and-restore modal */}
      {uploadOpen && (
        <Modal
          title="Restore from file"
          onClose={closeUploadRestore}
          wide
          icon={Upload}
        >
          <div className="space-y-4">
            <div className="rounded-xl border border-danger/30 bg-danger/10 p-2.5 text-xs text-danger">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Restore a downloaded <code>.hmsbkup</code> file. Choose a mode
                  below: merge into what is here, replace every table in the
                  archive, or wipe everything and load the archive cleanly. This
                  cannot be undone.
                </span>
              </div>
            </div>

            <label className="block cursor-pointer rounded-xl border border-dashed border-border bg-background/50 px-3 py-4 text-center transition-colors hover:border-primary/50">
              <input
                type="file"
                accept=".hmsbkup,application/x-firstaid-backup"
                className="hidden"
                onChange={(e) =>
                  handleUploadFile(e.target.files?.[0] ?? null)
                }
              />
              <Upload className="mx-auto mb-1.5 h-5 w-5 text-primary" />
              <span className="block text-xs font-medium text-text">
                {uploadInfo ? uploadInfo.filename : 'Choose a .hmsbkup file'}
              </span>
              <span className="mt-0.5 block text-[0.6875rem] text-text-muted">
                Upload the file exactly as it was downloaded. An interrupted
                download cannot be restored.
              </span>
            </label>

            {inspectUpload.isPending && (
              <p className="flex items-center gap-2 text-[0.6875rem] text-text-muted">
                <Loader2 size={13} className="animate-spin" />
                Reading archive…
              </p>
            )}

            {uploadInfo && (
              <>
                <ArchiveSummary
                  tables={uploadInfo.tableCount}
                  rows={
                    typeof uploadInfo.rowCount === 'number'
                      ? uploadInfo.rowCount
                      : null
                  }
                  files={uploadInfo.attachmentCount}
                  includeDatabase={uploadInfo.includeDatabase}
                  includeAttachments={uploadInfo.includeAttachments}
                  extra={
                    uploadInfo.includesAllModules
                      ? 'Full database backup'
                      : `${uploadInfo.modules.length} module(s)`
                  }
                />

                {uploadInfo.encrypted && (
                  <p className="text-[0.6875rem] text-success">
                    Encrypted archive — decrypted with this server&apos;s backup
                    key.
                  </p>
                )}

                {uploadDb &&
                  uploadInfo.includeDatabase &&
                  uploadInfo.rowCount === 0 && (
                    <div className="flex items-start gap-2 rounded-xl bg-warning/10 p-2.5 text-xs text-warning">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      <span>
                        This archive contains <strong>no data rows</strong> —
                        restoring it will leave the selected modules empty. Pick
                        a backup taken from the installation that holds the real
                        data.
                      </span>
                    </div>
                  )}

                <div className="grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/50 p-3 sm:grid-cols-3">
                  <Checkbox
                    checked={uploadDb}
                    onChange={(value) => {
                      setUploadDb(value);
                      setUploadPreview(null);
                    }}
                    disabled={!uploadInfo.includeDatabase}
                    label={
                      uploadInfo.includeDatabase
                        ? 'Database'
                        : 'No database in archive'
                    }
                  />
                  <Checkbox
                    checked={uploadFiles}
                    onChange={(value) => {
                      setUploadFiles(value);
                      setUploadPreview(null);
                    }}
                    disabled={!uploadInfo.includeAttachments}
                    label={
                      uploadInfo.includeAttachments
                        ? 'Attachments'
                        : 'No attachments in archive'
                    }
                  />
                  <Checkbox
                    checked={uploadMode === 'clean' ? true : uploadSafety}
                    onChange={(value) => {
                      if (uploadMode !== 'clean') setUploadSafety(value);
                    }}
                    label="Safety backup"
                    hint={
                      uploadMode === 'clean'
                        ? 'Always taken before a clean restore'
                        : undefined
                    }
                  />
                </div>

                {!uploadDb && uploadInfo.includeDatabase && (
                  <div className="flex items-start gap-2 rounded-xl bg-warning/10 p-2.5 text-xs text-warning">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>
                      <strong>Restore database is turned off.</strong> Only
                      attachments will be restored — patient, receipt and billing
                      data in this archive will be skipped.
                    </span>
                  </div>
                )}

                <RestoreModePicker
                  value={uploadMode}
                  onChange={(mode) => {
                    setUploadMode(mode);
                    setUploadConfirm('');
                    setUploadPreview(null);
                  }}
                  tables={Math.max(dataTableCount, uploadInfo.tableCount)}
                  attachments={uploadFiles && uploadInfo.includeAttachments}
                />

                {uploadDb &&
                  (uploadMode === 'merge' || uploadMode === 'skip') && (
                    <ModuleSelector
                      modules={uploadInfo.modules}
                      selected={uploadModules}
                      onChange={(next) => {
                        setUploadModules(next);
                        setUploadPreview(null);
                      }}
                    />
                  )}

                <RestorePreviewCard
                  preview={uploadPreview}
                  loading={previewUpload.isPending}
                  onPreview={runUploadPreview}
                  disabled={!uploadDb && !uploadFiles}
                />

                <RestoreConfirmInput
                  value={uploadConfirm}
                  onChange={setUploadConfirm}
                  keyword={uploadKeyword}
                  danger={uploadMode === 'clean'}
                />

                <ModalFooter onCancel={closeUploadRestore}>
                  <Button
                    variant="danger"
                    onClick={handleUploadRestore}
                    isLoading={restoreUpload.isPending}
                    disabled={
                      uploadConfirm.trim().toUpperCase() !== uploadKeyword ||
                      (!uploadDb && !uploadFiles)
                    }
                  >
                    {uploadMode === 'clean' ? (
                      <Eraser size={15} className="mr-1.5" />
                    ) : (
                      <RotateCcw size={15} className="mr-1.5" />
                    )}
                    {uploadMode === 'clean'
                      ? 'Wipe and restore'
                      : 'Restore now'}
                  </Button>
                </ModalFooter>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.length} backup${selectedIds.length === 1 ? '' : 's'}?`}
        description="The selected archives will be removed from their destinations and from the backups list. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length}`}
        loading={deleteBackups.isPending}
        onCancel={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
      />

      {/* Backup details */}
      {detailsRecord && (
        <Modal
          title="Backup details"
          onClose={() => setDetailsRecord(null)}
          wide
          icon={FileArchive}
        >
          {(() => {
            const meta = detailsRecord.metadata || {};
            const recordModules = Array.isArray(meta.modules)
              ? (meta.modules as string[])
              : [];
            const allModules =
              meta.includesAllModules === true || recordModules.length === 0;
            const badge = STATUS_BADGE[detailsRecord.status];
            return (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="break-all text-[0.8125rem] font-semibold text-text">
                      {detailsRecord.filename}
                    </p>
                    <p className="mt-0.5 text-[0.6875rem] text-text-muted">
                      {detailsRecord.trigger === 'scheduled'
                        ? 'Scheduled backup'
                        : 'Manual backup'}
                      {detailsRecord.createdByName
                        ? ` · ${detailsRecord.createdByName}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    {meta.partial && <Badge variant="warning">Partial</Badge>}
                    {meta.encrypted && <Badge variant="success">Encrypted</Badge>}
                    {meta.manifestUnread && (
                      <Badge variant="warning">Metadata unavailable</Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl border border-border bg-background/60 p-3 text-sm sm:grid-cols-3">
                  <DetailItem
                    icon={Calendar}
                    label="Created"
                    value={formatDateTime(detailsRecord.createdAt)}
                  />
                  <DetailItem
                    icon={Clock}
                    label="Duration"
                    value={formatDuration(
                      detailsRecord.startedAt,
                      detailsRecord.completedAt,
                    )}
                  />
                  <DetailItem
                    icon={Server}
                    label="Destination"
                    value={
                      PROVIDER_META[detailsRecord.provider]?.label ||
                      detailsRecord.provider
                    }
                  />
                  <DetailItem
                    icon={Archive}
                    label="Size"
                    value={formatBytes(detailsRecord.sizeBytes)}
                  />
                  <DetailItem
                    icon={FileText}
                    label="Tables"
                    value={
                      detailsRecord.includeDatabase
                        ? `${detailsRecord.tableCount} table(s)`
                        : 'Not included'
                    }
                  />
                  <DetailItem
                    icon={Hash}
                    label="Rows"
                    value={
                      typeof meta.rowCount === 'number'
                        ? meta.rowCount.toLocaleString()
                        : '—'
                    }
                  />
                  <DetailItem
                    icon={HardDriveDownload}
                    label="Attachments"
                    value={
                      detailsRecord.includeAttachments
                        ? `${detailsRecord.attachmentCount} file(s)`
                        : 'Not included'
                    }
                  />
                  <DetailItem
                    icon={User}
                    label="Started"
                    value={formatDateTime(detailsRecord.startedAt)}
                  />
                  <DetailItem
                    icon={CheckCircle2}
                    label="Completed"
                    value={formatDateTime(detailsRecord.completedAt)}
                  />
                </div>

                <div>
                  <p className="mb-1.5 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                    Data modules
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {allModules ? (
                      <Badge variant="default">Whole database (all modules)</Badge>
                    ) : (
                      recordModules.map((key) => (
                        <Badge key={key} variant="default">
                          {moduleList.find((module) => module.key === key)
                            ?.label || key}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                    Integrity &amp; storage
                  </p>
                  <div className="space-y-1.5 rounded-xl border border-border bg-background/60 p-2.5 text-[0.6875rem]">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-text-muted">Format</span>
                      <span className="text-text">
                        .hmsbkup v{meta.formatVersion ?? '—'}
                        {meta.encrypted ? ' · AES-256-GCM' : ' · unencrypted'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-text-muted">Checksum</span>
                      <span className="flex min-w-0 items-center gap-1.5">
                        <code className="truncate font-mono text-[0.625rem] text-text">
                          {detailsRecord.checksum || '—'}
                        </code>
                        {detailsRecord.checksum && (
                          <button
                            type="button"
                            title="Copy checksum"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(
                                  detailsRecord.checksum as string,
                                );
                                toast.success('Checksum copied');
                              } catch {
                                toast.error('Could not copy the checksum');
                              }
                            }}
                            className="shrink-0 rounded p-1 text-text-muted hover:bg-background hover:text-text"
                          >
                            <Copy size={12} />
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-text-muted">Stored at</span>
                      <span
                        className="truncate text-text"
                        title={detailsRecord.remotePath}
                      >
                        {detailsRecord.remotePath || '—'}
                      </span>
                    </div>
                    {detailsRecord.webUrl && (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-text-muted">Web link</span>
                        <a
                          href={detailsRecord.webUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 truncate text-primary hover:underline"
                        >
                          Open <ExternalLink size={11} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {(detailsRecord.errorMessage || meta.attachmentWarning) && (
                  <div className="space-y-1 rounded-xl border border-warning/30 bg-warning/10 p-2.5 text-[0.6875rem] text-warning">
                    {meta.attachmentWarning && (
                      <p>{String(meta.attachmentWarning)}</p>
                    )}
                    {detailsRecord.errorMessage && (
                      <p>{detailsRecord.errorMessage}</p>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReencrypt(detailsRecord)}
                    isLoading={reencryptBackup.isPending}
                    disabled={
                      detailsRecord.status !== 'completed' ||
                      !encryption.data?.data?.enabled
                    }
                    title={
                      encryption.data?.data?.enabled
                        ? 'Rewrite this archive with the current BACKUP_ENCRYPTION_KEY'
                        : 'Set BACKUP_ENCRYPTION_KEY on the server first'
                    }
                  >
                    <RefreshCw size={13} className="mr-1.5" />
                    Re-encrypt
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openRestore(detailsRecord)}
                    disabled={detailsRecord.status !== 'completed'}
                  >
                    <RotateCcw size={13} className="mr-1.5" />
                    Restore
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(detailsRecord)}
                    disabled={detailsRecord.status !== 'completed'}
                  >
                    <Download size={13} className="mr-1.5" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleVerify(detailsRecord)}
                    disabled={detailsRecord.status !== 'completed'}
                  >
                    <ShieldCheck size={13} className="mr-1.5" />
                    Verify
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDetailsRecord(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      <ConfirmDialog
        open={!!deleteBackupId}
        title="Delete this backup?"
        description="The archive will be removed from its destination and from the backups list. This cannot be undone."
        confirmLabel="Delete"
        loading={deleteBackup.isPending}
        onCancel={() => setDeleteBackupId(null)}
        onConfirm={() => {
          if (!deleteBackupId) return;
          deleteBackup.mutate(deleteBackupId, {
            onSuccess: () => {
              toast.success('Backup deleted');
              setDeleteBackupId(null);
            },
            onError: (err: any) => toast.error(err?.message || 'Delete failed'),
          });
        }}
      />

      <ConfirmDialog
        open={!!deleteProvider}
        title="Remove destination?"
        description="Credentials and settings for this destination will be deleted. Existing backup files are not removed."
        confirmLabel="Remove"
        loading={deleteDestination.isPending}
        onCancel={() => setDeleteProvider(null)}
        onConfirm={() => {
          if (!deleteProvider) return;
          deleteDestination.mutate(deleteProvider, {
            onSuccess: () => {
              toast.success('Destination removed');
              setDeleteProvider(null);
            },
            onError: (err: any) => toast.error(err?.message || 'Delete failed'),
          });
        }}
      />
    </div>
  );
}

// ───────────────────────────── Sub-components ─────────────────────────────

/** Compact KPI cell used in the overview strip. */
function Kpi({
  icon: Icon,
  label,
  value,
  hint,
  tone,
  small,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'danger';
  small?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 bg-surface px-3 py-2">
      <div
        className={cn(
          'rounded-lg p-1.5',
          tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon size={15} />
      </div>
      <div className="min-w-0">
        <p className="text-[0.625rem] font-medium uppercase tracking-wide text-text-muted">
          {label}
        </p>
        <p
          className={cn(
            'truncate font-semibold text-text',
            small ? 'text-xs' : 'text-base leading-tight',
          )}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </p>
        {hint && <p className="truncate text-[0.625rem] text-text-muted">{hint}</p>}
      </div>
    </div>
  );
}

/** Pill-style tab bar with optional counts/status dots. */
function SegmentedTabs({
  value,
  onChange,
  tabs,
}: {
  value: TabKey;
  onChange: (next: TabKey) => void;
  tabs: Array<{
    key: TabKey;
    label: string;
    icon: React.ElementType;
    badge?: number;
    dot?: boolean;
  }>;
}) {
  return (
    <div
      role="tablist"
      className="inline-flex items-center gap-0.5 rounded-xl border border-border bg-surface p-1 shadow-sm"
    >
      {tabs.map(({ key, label, icon: Icon, badge, dot }) => {
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
              active
                ? 'bg-primary text-white shadow-sm'
                : 'text-text-muted hover:bg-background hover:text-text',
            )}
          >
            <Icon size={14} />
            {label}
            {typeof badge === 'number' && badge > 0 && (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[0.625rem] font-semibold',
                  active ? 'bg-white/20 text-white' : 'bg-background text-text-muted',
                )}
              >
                {badge}
              </span>
            )}
            {dot && !active && (
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
            )}
          </button>
        );
      })}
    </div>
  );
}

function DestinationCard({
  destination,
  onConfigure,
  onTest,
  onConnect,
  onToggle,
  onRemove,
  busy,
}: {
  destination: BackupDestination;
  onConfigure: () => void;
  onTest: () => void;
  onConnect: () => void;
  onToggle: (patch: Partial<BackupDestination>) => void;
  onRemove: () => void;
  busy: boolean;
}) {
  const meta = PROVIDER_META[destination.provider];
  const Icon = meta.icon;

  const status = destination.connectionStatus;
  const statusBadge =
    status === 'connected'
      ? { variant: 'success' as const, label: 'Connected' }
      : status === 'error'
        ? { variant: 'danger' as const, label: 'Error' }
        : { variant: 'default' as const, label: 'Not tested' };

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-surface p-3.5 shadow-sm transition-colors hover:border-primary/40',
        !destination.isEnabled && 'opacity-70',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-sm font-semibold text-text">
              {destination.label}
            </h3>
            {destination.isDefault && <Badge variant="primary">Default</Badge>}
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
            {!destination.isEnabled && (
              <Badge variant="default">Disabled</Badge>
            )}
          </div>
          <p className="mt-0.5 line-clamp-2 text-[0.6875rem] leading-relaxed text-text-muted">
            {meta.description}
          </p>
          {destination.connectionMessage && (
            <p className="mt-1 line-clamp-2 text-[0.6875rem] text-text-muted">
              {destination.connectionMessage}
            </p>
          )}
          {destination.configReadable === false && (
            <p className="mt-1 flex items-start gap-1 text-[0.6875rem] text-danger">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>
                Saved credentials cannot be decrypted on this server (the backup
                key changed). Re-enter them and save, or restore the original
                key.
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <Button variant="outline" size="sm" onClick={onConfigure}>
          <Save size={13} className="mr-1" />
          Configure
        </Button>

        {meta.oauth ? (
          <Button variant="outline" size="sm" onClick={onConnect}>
            <Link2 size={13} className="mr-1" />
            Connect
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={onTest} disabled={busy}>
            <RefreshCw size={13} className="mr-1" />
            Test
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle({ isDefault: !destination.isDefault })}
          className={destination.isDefault ? 'text-warning' : ''}
          title="Set as the default destination"
        >
          <Star size={13} className="mr-1" />
          {destination.isDefault ? 'Default' : 'Make default'}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle({ isEnabled: !destination.isEnabled })}
          className={destination.isEnabled ? 'text-success' : ''}
        >
          <Power size={13} className="mr-1" />
          {destination.isEnabled ? 'Enabled' : 'Disabled'}
        </Button>

        {destination.isConnected && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="ml-auto text-danger"
          >
            <Trash2 size={13} className="mr-1" />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function SchedulePanel({
  schedule,
  loading,
  destinations,
  modules,
  saving,
  running,
  onSave,
  onRunNow,
}: {
  schedule: BackupSchedule | undefined;
  loading: boolean;
  destinations: BackupDestination[];
  modules: DataModule[];
  saving: boolean;
  running: boolean;
  onSave: (payload: Record<string, any>) => void;
  onRunNow: () => void;
}) {
  const [isEnabled, setIsEnabled] = useState(schedule?.isEnabled ?? false);
  const [frequency, setFrequency] = useState<BackupSchedule['frequency']>(
    schedule?.frequency ?? 'daily',
  );
  const [time, setTime] = useState(schedule?.time ?? '02:00');
  const [dayOfWeek, setDayOfWeek] = useState(schedule?.dayOfWeek ?? 0);
  const [dayOfMonth, setDayOfMonth] = useState(schedule?.dayOfMonth ?? 1);
  const [destinationId, setDestinationId] = useState(
    schedule?.destinationId || '',
  );
  const [includeDatabase, setIncludeDatabase] = useState(
    schedule?.includeDatabase ?? true,
  );
  const [includeAttachments, setIncludeAttachments] = useState(
    schedule?.includeAttachments ?? true,
  );
  const [selectedModules, setSelectedModules] = useState<string[]>(
    schedule?.modules || [],
  );
  const [keepLast, setKeepLast] = useState(schedule?.keepLast ?? 30);

  useEffect(() => {
    if (!schedule) return;
    setIsEnabled(schedule.isEnabled);
    setFrequency(schedule.frequency);
    setTime(schedule.time);
    setDayOfWeek(schedule.dayOfWeek);
    setDayOfMonth(schedule.dayOfMonth);
    setDestinationId(schedule.destinationId || '');
    setIncludeDatabase(schedule.includeDatabase);
    setIncludeAttachments(schedule.includeAttachments);
    setSelectedModules(schedule.modules || []);
    setKeepLast(schedule.keepLast);
  }, [schedule]);

  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  if (loading || !schedule) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-border bg-surface py-16 shadow-sm">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <div className="rounded-xl border border-border bg-surface shadow-sm lg:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3.5 py-2.5">
          <div>
            <h3 className="text-sm font-semibold text-text">
              Automatic schedule
            </h3>
            <p className="text-[0.6875rem] text-text-muted">
              Recurring backups run through the same queue as manual runs.
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-text">
            <span
              className={cn(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                isEnabled ? 'bg-success' : 'bg-border',
              )}
              aria-hidden
            >
              <span
                className={cn(
                  'inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform',
                  isEnabled ? 'translate-x-4.5' : 'translate-x-1',
                )}
              />
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            {isEnabled ? 'Enabled' : 'Disabled'}
          </label>
        </div>

        <div className="space-y-3 p-3.5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-text">
                Frequency
              </label>
              <select
                value={frequency}
                onChange={(e) =>
                  setFrequency(e.target.value as BackupSchedule['frequency'])
                }
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <TimePicker label="Time (24h)" value={time} onChange={setTime} />

            {frequency === 'weekly' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-text">
                  Day of week
                </label>
                <select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(Number(e.target.value))}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {weekdays.map((day, index) => (
                    <option key={day} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {frequency === 'monthly' && (
              <Input
                label="Day of month"
                type="number"
                min={1}
                max={28}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
              />
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-text">
                Destination
              </label>
              <select
                value={destinationId}
                onChange={(e) => setDestinationId(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Active destination (default)</option>
                {destinations
                  .filter((d) => d.isEnabled)
                  .map((d) => (
                    <option key={d.provider} value={d.id}>
                      {d.label}
                      {d.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
              </select>
            </div>

            <Input
              label="Keep last N scheduled backups"
              type="number"
              min={1}
              max={365}
              value={keepLast}
              onChange={(e) => setKeepLast(Number(e.target.value))}
            />
          </div>

          <div className="grid grid-cols-1 gap-2 rounded-xl border border-border bg-background/50 p-3 sm:grid-cols-2">
            <Checkbox
              checked={includeDatabase}
              onChange={setIncludeDatabase}
              label="Include database"
            />
            <Checkbox
              checked={includeAttachments}
              onChange={setIncludeAttachments}
              label="Include attachments"
            />
          </div>

          {includeDatabase && (
            <ModuleSelector
              modules={modules}
              selected={selectedModules}
              onChange={setSelectedModules}
            />
          )}

          {schedule.lastError && (
            <p className="rounded-lg bg-danger/10 px-2.5 py-1.5 text-[0.6875rem] text-danger">
              Last error: {schedule.lastError}
            </p>
          )}

          <p className="text-[0.6875rem] text-text-muted">
            Retention and “Run now” apply to the saved schedule — save your
            changes first. Manual backups and pre-restore safety snapshots are
            never pruned.
          </p>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={onRunNow}
              disabled={running}
              isLoading={running}
            >
              <Zap size={15} className="mr-1.5" />
              Run now
            </Button>
            <Button
              onClick={() =>
                onSave({
                  isEnabled,
                  frequency,
                  time,
                  dayOfWeek,
                  dayOfMonth,
                  destinationId: destinationId || null,
                  includeDatabase,
                  includeAttachments,
                  modules: includeDatabase ? selectedModules : [],
                  keepLast,
                })
              }
              isLoading={saving}
            >
              <Save size={15} className="mr-1.5" />
              Save schedule
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="rounded-xl border border-border bg-surface p-3.5 shadow-sm">
          <div className="flex items-center gap-2">
            <Clock size={15} className="text-primary" />
            <h3 className="text-sm font-semibold text-text">Next run</h3>
          </div>
          <p className="mt-2 text-sm font-semibold text-text">
            {schedule.isEnabled
              ? formatDateTime(schedule.nextRunAt || undefined)
              : 'Schedule disabled'}
          </p>
          <p className="mt-0.5 text-[0.6875rem] text-text-muted">
            Cron <code className="rounded bg-background px-1">{schedule.cronPattern}</code>{' '}
            · {schedule.timezone}
          </p>
          <dl className="mt-2.5 space-y-1.5 border-t border-border pt-2.5 text-[0.6875rem]">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-text-muted">Last success</dt>
              <dd className="text-text">
                {formatDateTime(schedule.lastSuccessAt)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-text-muted">Last run</dt>
              <dd className="text-text">{formatDateTime(schedule.lastRunAt)}</dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-text-muted">Consecutive failures</dt>
              <dd
                className={cn(
                  schedule.failureCount > 0 ? 'text-danger' : 'text-text',
                )}
              >
                {schedule.failureCount}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-xl border border-border bg-surface p-3.5 text-[0.6875rem] text-text-muted shadow-sm">
          <div className="mb-1 flex items-center gap-1.5 text-text">
            <Info size={13} className="text-primary" />
            <span className="font-medium">Timezone</span>
          </div>
          Follows the hospital timezone from{' '}
          <Link to="/settings/general" className="text-primary hover:underline">
            General Settings
          </Link>
          . Changing it re-registers the schedule automatically.
        </div>
      </div>
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
  wide,
  icon: Icon,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
  icon?: React.ElementType;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={cn(
          'flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl',
          wide ? 'max-w-3xl' : 'max-w-md',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            {Icon && <Icon size={16} className="text-primary" />}
            <h3 className="text-sm font-semibold text-text">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-text-muted transition-colors hover:bg-background hover:text-text"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>
        <div className="overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

/** Footer shared by the dialogs: cancel link on the left, actions on the right. */
function ModalFooter({
  children,
  onCancel,
}: {
  children: React.ReactNode;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
      <Button variant="ghost" onClick={onCancel} type="button">
        Cancel
      </Button>
      {children}
    </div>
  );
}

/** Summary chips describing what an archive contains. */
function ArchiveSummary({
  tables,
  rows,
  files,
  includeDatabase,
  includeAttachments,
  extra,
}: {
  tables: number;
  rows: number | null;
  files: number;
  includeDatabase: boolean;
  includeAttachments: boolean;
  extra?: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 rounded-xl border border-border bg-background/60 p-2.5 text-center">
      <div>
        <p className="text-sm font-semibold text-text">
          {includeDatabase ? tables : '—'}
        </p>
        <p className="text-[0.625rem] uppercase tracking-wide text-text-muted">
          Tables
        </p>
      </div>
      <div className="border-x border-border">
        <p className="text-sm font-semibold text-text">
          {rows === null ? '—' : rows.toLocaleString()}
        </p>
        <p className="text-[0.625rem] uppercase tracking-wide text-text-muted">
          Rows
        </p>
      </div>
      <div>
        <p className="text-sm font-semibold text-text">
          {includeAttachments ? files : '—'}
        </p>
        <p className="text-[0.625rem] uppercase tracking-wide text-text-muted">
          Files
        </p>
      </div>
      {extra && (
        <p className="col-span-3 border-t border-border pt-1.5 text-[0.6875rem] text-text-muted">
          {extra}
        </p>
      )}
    </div>
  );
}

/**
 * Group labels for the module picker. Mirrors the sidebar's information
 * architecture so operators find "Referrals" or "Expenses" where they expect.
 */
const MODULE_GROUP_LABELS: Record<string, string> = {
  clinical: 'Clinical',
  pharmacy: 'Pharmacy',
  finance: 'Finance',
  management: 'Management',
  website: 'Website',
  system: 'System',
};

/** Orders groups like the sidebar instead of catalogue insertion order. */
const MODULE_GROUP_ORDER = [
  'clinical',
  'pharmacy',
  'finance',
  'management',
  'website',
  'system',
];

/**
 * Module-wise selector shared by the create, schedule and restore flows.
 *
 * An empty selection means "all modules" (whole-database backup), which is the
 * default. Every module is checked in that state so the behaviour is obvious.
 */
function ModuleSelector({
  modules,
  selected,
  onChange,
  loading,
}: {
  modules: DataModule[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading?: boolean;
}) {
  const allKeys = modules.map((module) => module.key);
  const isAll = selected.length === 0;
  const isChecked = (key: string) => isAll || selected.includes(key);

  const toggle = (key: string) => {
    if (isAll) {
      // Start from "everything" and remove the one the user unchecked.
      onChange(allKeys.filter((k) => k !== key));
    } else if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      const next = [...selected, key];
      onChange(next.length === allKeys.length ? [] : next);
    }
  };

  const chosen = isAll
    ? modules
    : modules.filter((module) => selected.includes(module.key));
  const tableTotal = chosen.reduce((sum, module) => sum + module.tableCount, 0);

  const grouped = MODULE_GROUP_ORDER.map((key) => ({
    key,
    label: MODULE_GROUP_LABELS[key] || key,
    modules: modules.filter((module) => (module.group || 'system') === key),
  })).filter((group) => group.modules.length > 0);

  return (
    <div className="space-y-2 rounded-xl border border-border bg-background/60 p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-text">Data modules</p>
          <p className="text-[0.6875rem] text-text-muted">
            {isAll
              ? 'All modules (whole database) will be included.'
              : `${chosen.length} of ${modules.length} module(s) · ${tableTotal} table(s) selected.`}
          </p>
          {!isAll && (
            <p className="mt-0.5 text-[0.625rem] text-text-muted">
              Referenced tables (patients, employees, users, …) are handled
              automatically so a partial archive stays restorable.
            </p>
          )}
        </div>
        {!isAll && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange([])}
            type="button"
          >
            Select all
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      ) : modules.length === 0 ? (
        <p className="text-[0.6875rem] text-text-muted">No data modules available.</p>
      ) : (
        <div className="max-h-56 space-y-2.5 overflow-y-auto pr-1">
          {grouped.map((group) => (
            <div key={group.key}>
              <p className="mb-1 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                {group.label}
              </p>
              <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                {group.modules.map((module) => (
                  <label
                    key={module.key}
                    className="flex cursor-pointer items-start gap-2 text-xs text-text"
                    title={module.description}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked(module.key)}
                      onChange={() => toggle(module.key)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="min-w-0">
                      <span className="block truncate leading-tight">
                        {module.label}
                      </span>
                      <span className="block text-[0.625rem] text-text-muted">
                        {module.tableCount} table(s)
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Checkbox({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label
        className={cn(
          'flex items-center gap-2 text-xs',
          disabled
            ? 'cursor-not-allowed text-text-muted opacity-60'
            : 'cursor-pointer text-text',
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary disabled:cursor-not-allowed"
        />
        {label}
      </label>
      {hint && (
        <p className="ml-5.5 text-[0.625rem] leading-relaxed text-text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * Selects what a restore does to the data that is already in the database.
 * "Clean" is deliberately loud: it empties every data table first.
 */
/**
 * Read-only dry-run summary: what the restore would add, overwrite and delete
 * in each table. Shown before the confirmation phrase so the operator can see
 * the blast radius without running anything.
 */
function RestorePreviewCard({
  preview,
  loading,
  onPreview,
  disabled,
}: {
  preview: RestorePreview | null;
  loading: boolean;
  onPreview: () => void;
  disabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const tables = preview?.tables ?? [];
  const totals = preview?.totals;
  const visible = expanded ? tables : tables.slice(0, 8);

  return (
    <div className="rounded-xl border border-border bg-background/50 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-text">Preview changes</p>
          <p className="text-[0.6875rem] text-text-muted">
            Read-only — nothing is written, deleted or backed up.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={onPreview}
          isLoading={loading}
          disabled={disabled}
        >
          <Eye size={14} className="mr-1.5" />
          Preview
        </Button>
      </div>

      {preview && (
        <div className="mt-2.5 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5 text-[0.6875rem]">
            <span className="rounded bg-success/10 px-2 py-0.5 text-success">
              +{totals?.add ?? 0} added
            </span>
            <span className="rounded bg-primary/10 px-2 py-0.5 text-primary">
              ~{totals?.update ?? 0} overwritten
            </span>
            <span className="rounded bg-danger/10 px-2 py-0.5 text-danger">
              −{totals?.delete ?? 0} deleted
            </span>
            <span className="rounded bg-background px-2 py-0.5 text-text-muted">
              mode: {preview.mode}
            </span>
          </div>

          {totals &&
            totals.add + totals.update + totals.delete === 0 && (
              <p className="text-[0.6875rem] text-text-muted">
                No rows differ — this restore would not change anything.
              </p>
            )}

          {tables.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-[0.6875rem]">
                <thead className="bg-background text-text-muted">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Table</th>
                    <th className="px-2 py-1 text-right font-medium">Add</th>
                    <th className="px-2 py-1 text-right font-medium">
                      Overwrite
                    </th>
                    <th className="px-2 py-1 text-right font-medium">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.table} className="border-t border-border/60">
                      <td className="px-2 py-1 font-mono">
                        {row.table}
                        {row.approximate ? ' *' : ''}
                      </td>
                      <td className="px-2 py-1 text-right text-success">
                        +{row.add}
                      </td>
                      <td className="px-2 py-1 text-right text-primary">
                        ~{row.update}
                      </td>
                      <td className="px-2 py-1 text-right text-danger">
                        −{row.delete}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tables.length > 8 && (
                <button
                  type="button"
                  className="w-full border-t border-border px-2 py-1 text-[0.6875rem] text-primary hover:underline"
                  onClick={() => setExpanded((value) => !value)}
                >
                  {expanded
                    ? 'Show fewer tables'
                    : `Show all ${tables.length} tables`}
                </button>
              )}
            </div>
          )}

          {preview.warnings.map((warning) => (
            <p key={warning} className="text-[0.6875rem] text-warning">
              {warning}
            </p>
          ))}

          {preview.dependencyTables.length > 0 && (
            <p className="text-[0.6875rem] text-text-muted">
              Parent tables included automatically:{' '}
              {preview.dependencyTables.join(', ')}
            </p>
          )}

          <p className="text-[0.6875rem] text-text-muted">
            Counts are computed against the current database. Re-run the preview
            if you change the mode, modules or checkboxes.
          </p>
        </div>
      )}
    </div>
  );
}

function RestoreModePicker({
  value,
  onChange,
  tables,
  attachments,
}: {
  value: RestoreMode;
  onChange: (next: RestoreMode) => void;
  tables: number;
  attachments: boolean;
}) {
  const options: Array<{
    key: RestoreMode;
    label: string;
    description: string;
    icon: React.ElementType;
    danger?: boolean;
  }> = [
    {
      key: 'merge',
      label: 'Merge',
      description: 'Update existing records and add missing ones. Nothing is deleted.',
      icon: Merge,
    },
    {
      key: 'skip',
      label: 'Add missing',
      description: 'Keep existing records untouched; only insert what is missing.',
      icon: Plus,
    },
    {
      key: 'replace',
      label: 'Full replace',
      description: 'Empty every table in this archive, then import it.',
      icon: RotateCcw,
    },
    {
      key: 'clean',
      label: 'Clean restore',
      description: 'Delete ALL data and old files, then import this archive.',
      icon: Eraser,
      danger: true,
    },
  ];

  return (
    <div className="space-y-1.5">
      <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
        Restore mode
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {options.map((option) => {
          const Icon = option.icon;
          const active = value === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(option.key)}
              aria-pressed={active}
              className={cn(
                'flex items-start gap-2 rounded-xl border p-2.5 text-left transition-colors',
                active
                  ? option.danger
                    ? 'border-danger/60 bg-danger/10'
                    : 'border-primary/60 bg-primary/10'
                  : 'border-border bg-surface hover:border-primary/40',
              )}
            >
              <Icon
                size={14}
                className={cn(
                  'mt-0.5 shrink-0',
                  active
                    ? option.danger
                      ? 'text-danger'
                      : 'text-primary'
                    : 'text-text-muted',
                )}
              />
              <span className="min-w-0">
                <span
                  className={cn(
                    'block text-xs font-semibold',
                    active && option.danger ? 'text-danger' : 'text-text',
                  )}
                >
                  {option.label}
                </span>
                <span className="block text-[0.6875rem] leading-snug text-text-muted">
                  {option.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {value === 'clean' && (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-2.5 text-[0.6875rem] leading-relaxed text-danger">
          <div className="flex items-start gap-1.5">
            <ShieldAlert size={13} className="mt-0.5 shrink-0" />
            <span>
              <strong>Destructive.</strong> Every row in {tables} data table(s)
              is deleted and{' '}
              {attachments
                ? 'stored attachment files the archive does not contain are removed'
                : 'attachments are left as they are'}{' '}
              before the archive is imported. A safety backup is always taken
              first, and the whole wipe + import runs in one database
              transaction (rolled back on any error). Backups, destinations and
              the schedule are preserved.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Confirmation input for destructive restores. The required keyword sits next
 * to a copy button so the operator does not have to retype it by hand.
 */
function RestoreConfirmInput({
  value,
  onChange,
  keyword,
  danger,
}: {
  value: string;
  onChange: (next: string) => void;
  keyword: string;
  danger?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copyKeyword = async () => {
    try {
      await navigator.clipboard.writeText(keyword);
      toast.success(`Copied "${keyword}"`);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <div className="relative">
      <Input
        label={`Type "${keyword}" to confirm`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={keyword}
        className={cn('pr-10', danger && 'border-danger/60')}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        title={`Copy ${keyword}`}
        aria-label={`Copy ${keyword}`}
        onClick={copyKeyword}
        className="absolute bottom-0 right-1 flex h-[38px] w-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-background hover:text-text"
      >
        {copied ? (
          <CheckCircle2 size={14} className="text-success" />
        ) : (
          <Copy size={13} />
        )}
      </button>
    </div>
  );
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={13} className="mt-0.5 shrink-0 text-text-muted" />
      <div className="min-w-0">
        <p className="text-[0.625rem] uppercase tracking-wide text-text-muted">
          {label}
        </p>
        <p className="truncate text-xs text-text" title={String(value)}>
          {value}
        </p>
      </div>
    </div>
  );
}

function IconAction({
  title,
  children,
  onClick,
  disabled,
  danger,
}: {
  title: string;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-lg p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-30',
        danger
          ? 'text-danger hover:bg-danger/10'
          : 'text-text-muted hover:bg-background hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Live progress card for the async backup run: progress bar, phase and
 * counters, plus pause / resume / cancel / remove controls.
 */
function ActiveRunCard({
  title = 'Backup run',
  subtitle,
  run,
  isLoading,
  busy,
  onClose,
  onPause,
  onResume,
  onCancel,
  onDestroy,
  onRetry,
}: {
  title?: string;
  subtitle?: string;
  run?: { state?: string; progress?: BackupProgress | null };
  isLoading: boolean;
  busy: boolean;
  onClose: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onDestroy: () => void;
  onRetry: () => void;
}) {
  const state = run?.state || 'queued';
  const progress = run?.progress || null;
  const terminal = ['completed', 'failed', 'cancelled', 'unknown'].includes(
    state,
  );
  const active = !terminal;
  const paused = state === 'paused';
  const percent = terminal
    ? state === 'completed'
      ? 100
      : 0
    : Math.max(0, Math.min(100, progress?.percent ?? 0));

  const stateLabel: Record<string, string> = {
    queued: 'Queued',
    waiting: 'Queued',
    delayed: 'Scheduled',
    running: 'Running',
    active: 'Running',
    paused: 'Paused',
    cancelling: 'Cancelling…',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    unknown: 'Unknown',
  };

  const variant: 'success' | 'danger' | 'warning' | 'default' =
    state === 'completed'
      ? 'success'
      : state === 'failed'
        ? 'danger'
        : terminal
          ? 'default'
          : 'warning';

  return (
    <div className="rounded-xl border border-primary/40 bg-surface p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="rounded-lg bg-primary/10 p-1.5">
            <Activity className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text">{title}</p>
            <p className="truncate text-[0.6875rem] text-text-muted">
              {subtitle || 'preparing…'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={variant}>{stateLabel[state] || state}</Badge>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-text-muted hover:bg-background"
            title="Hide"
            aria-label="Hide"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-background">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              state === 'failed'
                ? 'bg-danger'
                : state === 'cancelled'
                  ? 'bg-text-muted'
                  : 'bg-primary',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-[0.6875rem] text-text-muted">
          <span>{progress?.message || stateLabel[state] || 'Working…'}</span>
          <span className="tabular-nums">{percent}%</span>
        </div>

        {active && progress?.current && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background/70 px-2.5 py-1.5">
            <Loader2 size={12} className="shrink-0 animate-spin text-primary" />
            <code className="truncate text-[0.6875rem] font-medium text-text">
              {progress.current}
            </code>
          </div>
        )}
      </div>

      <div className="mt-2.5 grid grid-cols-2 gap-1.5 text-[0.6875rem] text-text-muted sm:grid-cols-4">
        <div>
          Tables:{' '}
          <span className="text-text">
            {progress?.tablesDone ?? 0}/{progress?.tablesTotal ?? '—'}
          </span>
        </div>
        <div>
          Rows: <span className="text-text">{progress?.rows ?? 0}</span>
        </div>
        <div>
          Files:{' '}
          <span className="text-text">
            {progress?.attachmentsDone ?? 0}/
            {progress?.attachmentsTotal ?? '—'}
          </span>
        </div>
        <div>
          Phase: <span className="text-text">{progress?.phase ?? '—'}</span>
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {(state === 'running' || state === 'active') && (
          <Button variant="outline" size="sm" onClick={onPause} disabled={busy}>
            <Pause size={13} className="mr-1" /> Pause
          </Button>
        )}
        {paused && (
          <Button variant="outline" size="sm" onClick={onResume} disabled={busy}>
            <Play size={13} className="mr-1" /> Resume
          </Button>
        )}
        {active && (
          <Button
            variant="outline"
            size="sm"
            className="text-danger"
            onClick={onCancel}
            disabled={busy}
          >
            <Square size={13} className="mr-1" /> Cancel
          </Button>
        )}
        {state === 'failed' && (
          <Button variant="outline" size="sm" onClick={onRetry} disabled={busy}>
            <RefreshCw size={13} className="mr-1" /> Retry
          </Button>
        )}
        {terminal && (
          <Button variant="outline" size="sm" onClick={onDestroy} disabled={busy}>
            <XCircle size={13} className="mr-1" /> Remove job
          </Button>
        )}
        {isLoading && (
          <Loader2 size={14} className="animate-spin text-primary" />
        )}
      </div>
    </div>
  );
}
